// ================================================================
// src/pages/AdminPrivateClassPage.jsx
// نسخه‌ی نهایی — همه‌ی ۴۳ مشکل حل شده
// ================================================================

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  memo,
} from 'react';
import moment from 'moment-jalaali';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './PrivateClassPage.css';

import {
  useTheme,
  useToast,
  useData,
  useScrollProgress,
  useSearchShortcut,
  gradeOptions,
  fieldOptions,
  capacityOptions,
  getRoomId,
  Modal,
  PersianDatePicker,
  PageParticles,
  DEFAULT_ROOM_FORM,
  DEFAULT_EDIT_ROOM_FORM,
  DEFAULT_NEW_USER,
  useRoomeetAuthGate,
  AuthGate,
  PrivateClassProviders,
  COURSE_STATUS,
  getCourseStatusLabel,
  canStartCourse,
  computeCourseCommissionPending,
  getCommissionRate,
  computeCommissionSplit,
  computePaymentSplit,
  VAT_RATE,
  CANCEL_PENALTY_RATE,
} from './PrivateClassShared';

// ================================================================
// پنل ادمین
// ================================================================
function AdminPanel({
  user,
  state,
  dispatch,
  refreshData,
  showToast,
  internalApi,
  roomeetApi,
  loading,
  isRoomeetReady,
}) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [settings, setSettings] = useState(state.adminSettings);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [allRooms, setAllRooms] = useState([]);
  const [onlineRooms, setOnlineRooms] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedSaleId, setSelectedSaleId] = useState('');
  const [roomModal, setRoomModal] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({ ...DEFAULT_NEW_USER });
  const [recordingsModal, setRecordingsModal] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [roomSearchKeyword, setRoomSearchKeyword] = useState('');

  const [showStudentsModalAdmin, setShowStudentsModalAdmin] = useState(false);
  const [selectedCourseStudentsAdmin, setSelectedCourseStudentsAdmin] = useState([]);
  const [selectedCourseTitleAdmin, setSelectedCourseTitleAdmin] = useState('');

  const [roomForm, setRoomForm] = useState({ ...DEFAULT_ROOM_FORM });
  const [selectedRoomForRecording, setSelectedRoomForRecording] = useState('');
  const isStartingRef = useRef(false);
  const isUpdatingRef = useRef(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [editUserForm, setEditUserForm] = useState(null);
  const [forceEnrollModal, setForceEnrollModal] = useState(false);
  const [forceEnrollCourse, setForceEnrollCourse] = useState(null);
  const [forceEnrollStudents, setForceEnrollStudents] = useState([]);
  const [bulkNotificationModal, setBulkNotificationModal] = useState(false);
  const [bulkNotificationData, setBulkNotificationData] = useState({
    message: '',
    type: 'info',
    role: '',
  });
  const [transactionFilters, setTransactionFilters] = useState({
    type: '',
    dateFrom: '',
    dateTo: '',
  });
  // ✅ مشکل ۱: default = 'scheduled' (که شامل scheduled+live هست)
  const [filterStatus, setFilterStatus] = useState('scheduled');
  const [editingRequest, setEditingRequest] = useState(null);
  const [editRequestForm, setEditRequestForm] = useState(null);
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [editRoomForm, setEditRoomForm] = useState({ ...DEFAULT_EDIT_ROOM_FORM });
  const [showEditRoomModal, setShowEditRoomModal] = useState(false);
  // ✅ D32: نمایش مودال create-room با advanced options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  // 🕒 ticker برای countdown
  const [nowMoment, setNowMoment] = useState(moment());
  useEffect(() => {
    const interval = setInterval(() => setNowMoment(moment()), 1000);
    return () => clearInterval(interval);
  }, []);

  const users = state.users;
  const pendingRequests = state.requests.filter((r) => r.status === 'pending');
  const failedRequests = state.requests.filter((r) => r.status === 'failed');
  const transactions = state.transactions;
  const courses = state.courses;

  const totalUserPages = Math.ceil(users.length / perPage);
  const paginatedUsers = users.slice((page - 1) * perPage, page * perPage);

  // ============================================================
  // بارگذاری داده‌های Roomeet
  // ============================================================
  const loadRoomeetData = useCallback(async () => {
    if (!isRoomeetReady || isUpdatingRef.current) return;
    isUpdatingRef.current = true;
    try {
      const [roomsRes, onlineRes, svcsRes] = await Promise.allSettled([
        roomeetApi.getAllRooms(1, 100),
        roomeetApi.getOnlineRooms(),
        roomeetApi.getServices(),
      ]);

      const roomsData = roomsRes.status === 'fulfilled' ? roomsRes.value : { meetings: [] };
      const onlineData = onlineRes.status === 'fulfilled' ? onlineRes.value : { onlineMeetings: [] };
      const svcsData = svcsRes.status === 'fulfilled' ? svcsRes.value : { packages: [] };

      setAllRooms(roomsData.meetings || []);
      setOnlineRooms(onlineData.onlineMeetings || []);

      const pkgs = svcsData.packages || [];
      setServices(pkgs);
      if (pkgs.length) {
        setSelectedSaleId((prev) => prev || pkgs[0].saleId);
        setSettings((prev) =>
          prev.defaultSaleId ? prev : { ...prev, defaultSaleId: pkgs[0].saleId }
        );
      }
    } catch (e) {
      console.error('خطا در loadRoomeetData:', e);
    } finally {
      isUpdatingRef.current = false;
    }
  }, [isRoomeetReady, roomeetApi]);

  useEffect(() => {
    loadRoomeetData();
  }, [loadRoomeetData]);

  useEffect(() => {
    if (!isRoomeetReady) return undefined;
    const interval = setInterval(loadRoomeetData, 15000);
    return () => clearInterval(interval);
  }, [isRoomeetReady, loadRoomeetData]);

  // ============================================================
  // ✅ helper: کاهش pendingAmount در data (بدون ذخیره)
  // ============================================================
  const _decreasePendingInData = (data, teacherId, amount) => {
    if (!teacherId || !amount || amount <= 0) return;
    const teacher = data.users.find((u) => u.id === teacherId);
    if (teacher) {
      teacher.pendingAmount = Math.max(0, (teacher.pendingAmount || 0) - amount);
    }
  };

  // ============================================================
  // ✅ helper: batch notification
  // ============================================================
  const sendBatchNotifications = async (notifications) => {
    if (!notifications || notifications.length === 0) return;
    const data = await internalApi._fetchData(true);
    const now = moment().format('jYYYY-jMM-jDD');
    for (const n of notifications) {
      data.notifications.push({
        id: Date.now() + Math.random() + Math.random(),
        message: n.message,
        type: n.type || 'info',
        read: false,
        date: now,
        userId: n.userId || null,
      });
    }
    await internalApi._saveData(data);
  };

  // ============================================================
  // ✅ A12 + B2 + B5 + E37: handleDeleteUser — بازنویسی کامل
  // ============================================================
const handleDeleteUser = async (userId) => {
  const targetUser = state.users.find((u) => u.id === userId);
  if (!targetUser) {
    showToast('کاربر یافت نشد', 'error');
    return;
  }

  // ✅ تأیید اول
  if (
    !window.confirm(
      `آیا از حذف کاربر «${targetUser.name}» مطمئن هستید؟`
    )
  )
    return;

  // ✅ تأیید دوم — نوشتاری و جدی
  const typed = window.prompt(
    `⚠️ هشدار جدی — حذف دائمی و غیرقابل بازگشت!\n\nتمام داده‌های «${targetUser.name}» از جمله:\n` +
    `• حساب کاربری و پروفایل\n` +
    `• کلاس‌ها و ثبت‌نام‌ها\n` +
    `• تراکنش‌ها و کیف پول\n` +
    `• داده‌های Roomeet\n` +
    `• پیشرفت درسی، آمار و جوایز\n\n` +
    `برای همیشه از سیستم حذف خواهد شد.\n\n` +
    `برای تأیید نهایی، کلمه «حذف» را تایپ کنید:`
  );
  if (typed !== 'حذف') {
    if (typed !== null) {
      showToast('عبارت تأیید نادرست است. حذف لغو شد.', 'warning');
    }
    return;
  }

  try {


      // ─── سناریو ۱: دانش‌آموز ───
      if (targetUser.role === 'student' && targetUser.enrolledCourses) {
        for (const courseId of targetUser.enrolledCourses) {
          const course = state.courses.find((c) => c.id === courseId);
          if (!course) continue;

          const notifs = [];
          if (course.status !== COURSE_STATUS.CANCELLED) {
            // ✅ ۱) اول snapshot رو capture کن (قبل از حذف از enrolledStudents)
            const studentEntry = (course.enrolledStudents || [])
              .find((s) => s.userId === userId);
            const fullRefund = studentEntry && typeof studentEntry.totalPaid === 'number'
              ? studentEntry.totalPaid
              : Math.round((course.price || 0) * (1 + VAT_RATE / 100));

            const netForThisStudent = studentEntry && typeof studentEntry.netToTeacher === 'number'
              ? studentEntry.netToTeacher
              : Math.round((course.price || 0) * (1 - (state.adminSettings?.commission || 0) / 100));

            // ✅ ۲) refund کامل به دانش‌آموز
            await internalApi.updateUser(userId, {
              wallet: (targetUser.wallet || 0) + fullRefund,
            });

            // ✅ ۳) حذف دانش‌آموز از کلاس
            await internalApi.updateCourse(courseId, {
              enrolled: Math.max(0, (course.enrolled || 0) - 1),
              enrolledStudents: (course.enrolledStudents || []).filter(
                (s) => s.userId !== userId
              ),
            });

            // ✅ ۴) کاهش pending معلم با مبلغ snapshot شده
            const data = await internalApi._fetchData(true);
            const realCourse = data.courses.find((c) => c.id === courseId);
            if (realCourse && !realCourse.pendingReleased) {
              _decreasePendingInData(data, realCourse.teacherId, netForThisStudent);
              await internalApi._saveData(data);
            }

            // ✅ ۵) تراکنش refund
            await internalApi.addTransaction({
              userId,
              amount: fullRefund,
              date: moment().format('jYYYY-jMM-jDD'),
              type: 'refund',
              courseId: course.id,
              courseTitle: course.title,
              note: 'حذف کاربر توسط ادمین — بازگشت کامل',
            });

            notifs.push({
              message: `دانش‌آموز ${targetUser.name} از کلاس "${course.title}" حذف شد.`,
              type: 'info',
              userId: course.teacherId,
            });
          } else {
            // کلاس لغو شده — فقط از enrolledStudents پاک کن
            await internalApi.updateCourse(courseId, {
              enrolledStudents: (course.enrolledStudents || []).filter(
                (s) => s.userId !== userId
              ),
            });
          }
          if (notifs.length) {
            await sendBatchNotifications(notifs);
          }
        }
      }

      // ─── سناریو ۲: معلم ───
      if (targetUser.role === 'teacher') {
        const userRequests = state.requests.filter((r) => r.teacherId === userId);
        for (const req of userRequests) await internalApi.deleteRequest(req.id);

        const userCourses = state.courses.filter((c) => c.teacherId === userId);
        for (const course of userCourses) {
          // ✅ فقط کافیست deleteCourse را صدا بزنید؛ خودش وجه دانش‌آموزان را برمی‌گرداند
          await internalApi.deleteCourse(course.id);
        }
      }

      await internalApi.deleteUser(userId);
      await refreshData();
      showToast('کاربر و تمام داده‌های مرتبط حذف شد', 'success');
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUser.name || !newUser.phone) {
      showToast('لطفاً نام و شماره تلفن را وارد کنید', 'error');
      return;
    }
    setIsLoading(true);
    try {
      const userData = {
        name: newUser.name,
        phone: newUser.phone,
        password: newUser.password || '123456',
        role: newUser.role,
        wallet: newUser.wallet || 0,
        email: `${newUser.phone}@example.com`,
        active: true,
        enrolledCourses: [],
        createdAt: new Date().toISOString(),
      };
      const createdUser = await internalApi.addUser(userData);
      if (createdUser.roomeetMemberId) {
        showToast(`✅ کاربر ${newUser.name} به Roomeet هم اضافه شد`, 'success');
      } else {
        showToast(
          `⚠️ کاربر ذخیره شد ولی همگام‌سازی Roomeet ناموفق بود`,
          'warning',
          10000
        );
      }
      setAddUserModal(false);
      setNewUser({ ...DEFAULT_NEW_USER });
      await refreshData();
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUserStatus = async (userId) => {
    const u = state.users.find((x) => x.id === userId);
    if (!u) return;
    try {
      await internalApi.updateUser(userId, { active: !(u.active !== false) });
      await refreshData();
      showToast(
        `وضعیت ${u.name} به ${!(u.active !== false) ? 'فعال' : 'غیرفعال'} تغییر کرد`,
        'info'
      );
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  const handleEditUser = (u) => {
    setEditUserForm({ ...u });
    setEditUserModal(true);
  };

  // ✅ A10: تغییر نقش → پاک‌سازی داده‌های قدیمی
  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    try {
      const oldUser = state.users.find((u) => u.id === editUserForm.id);
      const oldRole = oldUser?.role;
      const newRole = editUserForm.role;

      const updates = {
        name: editUserForm.name,
        phone: editUserForm.phone,
        email: editUserForm.email,
        role: newRole,
        active: editUserForm.active,
      };

      // ✅ A10: اگر نقش تغییر کرد، داده‌های مخصوص نقش قدیم را پاک کن
      if (oldRole !== newRole) {
        if (oldRole === 'student') {
          updates.enrolledCourses = [];
        }
        if (oldRole === 'teacher') {
          updates.pendingAmount = 0;
        }
      }

      await internalApi.updateUser(editUserForm.id, updates);
      await refreshData();
      showToast('کاربر با موفقیت ویرایش شد', 'success');
      setEditUserModal(false);
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  // ============================================================
  // سینک کامل
  // ============================================================
  const handleFullSync = async () => {
    if (!isRoomeetReady) {
      showToast('اتصال به Roomeet برقرار نیست', 'error');
      return;
    }
    if (
      !window.confirm(
        'همه کاربران و عضویت اتاق‌ها با Roomeet سینک شوند؟\nممکن است چند دقیقه طول بکشد.'
      )
    )
      return;

    setIsSyncing(true);
    try {
      showToast('🔄 مرحله ۱/۲: سینک کاربران...', 'info', 3000);
      const userResult = await internalApi.syncAllUsersToRoomeet(
        (current, total, name) => {
          if (current % 5 === 0 || current === total) {
            showToast(`کاربر ${current}/${total}: ${name}`, 'info', 1200);
          }
        }
      );

      if (userResult.error) {
        showToast(`❌ ${userResult.error}`, 'error', 10000);
      } else if (userResult.allSynced) {
        showToast('✅ همه کاربران از قبل سینک بودند', 'info', 3000);
      } else if (userResult.total > 0) {
        showToast(
          `✅ ${userResult.success} کاربر اضافه شد${userResult.failed > 0 ? `، ${userResult.failed} ناموفق` : ''}`,
          userResult.failed > 0 ? 'warning' : 'success',
          5000
        );
      } else {
        showToast('ℹ️ کاربری برای سینک نیست', 'info', 3000);
      }

      showToast('🔄 مرحله ۲/۲: سینک عضویت اتاق‌ها...', 'info', 3000);
      const roomResult = await internalApi.syncAllRoomMemberships();

      if (roomResult.error) {
        showToast(`⚠️ ${roomResult.error}`, 'warning', 8000);
      } else if (roomResult.added > 0) {
        showToast(`✅ ${roomResult.added} عضویت جدید اضافه شد`, 'success', 6000);
      } else {
        showToast('ℹ️ همه عضویت‌ها از قبل سینک بودند', 'info', 4000);
      }

      await refreshData();
      await loadRoomeetData();
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error', 8000);
    } finally {
      setIsSyncing(false);
    }
  };

  // ============================================================
  // ✅ E38: تأیید درخواست + notify ادمین‌های دیگر
  // ============================================================
  const handleApprove = async (id, retry = false) => {
    if (isLoading) return;
    if (!isRoomeetReady) {
      showToast('اتصال به رومیت برقرار نیست.', 'error');
      return;
    }
    if (!window.confirm('آیا می‌خواهید این کلاس در رومیت تشکیل شود؟')) return;
    setIsLoading(true);
    try {
      const req = state.requests.find((r) => r.id === id);
      if (!req) {
        showToast('درخواست یافت نشد', 'error');
        return;
      }
      const teacher = state.users.find((u) => u.id === req.teacherId);
      if (!teacher) {
        showToast('معلم یافت نشد', 'error');
        return;
      }

      if (retry) {
        const existingCourse = state.courses.find(
          (c) =>
            c.teacherId === req.teacherId &&
            c.title === req.title &&
            c.date === req.date &&
            c.time === req.time &&
            c.siroomehClassId
        );
        if (existingCourse) {
          await internalApi.updateRequest(id, {
            status: 'approved',
            errorMessage: null,
          });
          await refreshData();
          showToast('✅ کلاس از قبل در رومیت ایجاد شده بود', 'success');
          return;
        }
      }

      const teacherMemberId = await internalApi.ensureUserInRoomeet(teacher.id);
      if (!teacherMemberId) {
        showToast(
          'امکان افزودن معلم به Roomeet وجود ندارد. لطفاً اتصال را بررسی کنید.',
          'error'
        );
        return;
      }

      const saleId = settings.defaultSaleId || selectedSaleId;
      if (!saleId) {
        showToast('هیچ بسته سرویسی یافت نشد.', 'error');
        return;
      }

      const conflict = state.courses.some(
        (c) =>
          c.teacherId === req.teacherId &&
          c.date === req.date &&
          c.time === req.time &&
          c.status !== COURSE_STATUS.CANCELLED &&
          c.status !== COURSE_STATUS.ENDED
      );
      if (conflict) {
        showToast('معلم در این زمان کلاس دیگری دارد.', 'error');
        return;
      }

      const roomData = {
        name: req.title,
        guest: true,
        welcomeMessage: `به کلاس ${req.title} خوش آمدید`,
        moderatorOnlyMessage: 'پیام ویژه مدیر',
        startWithModerator: true,
        duration: req.duration || 60,
        language: 'fa',
        allowDownloadRecordings: true,
        webcamsOnlyForModerat: true,
        autoStartRecording: false,
        muteOnStart: false,
        allowModsToEjectCameras: true,
        disablePresentation: false,
        logoutURL: '',
        lockSettingsDisablePrivateChat: false,
        lockSettingsDisablePublicChat: false,
        lockSettingsHideUserList: false,
        endWhenNoModeratorDelayInMinutes: 15,
      };
      const roomResult = await roomeetApi.createRoom(saleId, roomData);
      const theIdOfMeeting = roomResult.theIdOfMeeting;

      try {
        await roomeetApi.addMembersToRoom(theIdOfMeeting, [
          { memberId: teacherMemberId, role: 'teacher' },
        ]);
      } catch (e) {
        console.warn('addMembersToRoom in approve:', e.message);
      }

      await internalApi.updateRequest(id, { status: 'approved', errorMessage: null });

      const newCourse = {
        id: Date.now(),
        title: req.title,
        teacherId: req.teacherId,
        teacher: teacher.name,
        rating: 0,
        ratingCount: 0,
        price: req.price,
        grade: req.grade,
        field: req.field || '',
        subject: req.subject,
        date: req.date,
        time: req.time,
        duration: req.duration || 60,
        capacity: req.capacity,
        enrolled: 0,
        icon: 'fa-chalkboard',
        color: '#0b2b5c',
        sponsored: false,
        popular: false,
        status: COURSE_STATUS.SCHEDULED,
        siroomehClassId: theIdOfMeeting,
        siroomehLink: `https://roomeet.ir/join/${theIdOfMeeting}`,
        siroomehSettings: roomData,
        enrolledStudents: [],
        teacherRoomeetMemberId: teacherMemberId,
        roomIsLive: false,
        roomOfflineSince: null,
        refundProcessed: false,
      };
      await internalApi.addCourse(newCourse);
      dispatch({ type: 'ADD_COURSE', payload: newCourse });

      // ✅ E38: notify معلم + بقیه ادمین‌ها
      const notifs = [
        {
          message: `✅ کلاس "${req.title}" تأیید و در رومیت ایجاد شد.`,
          type: 'success',
          userId: req.teacherId,
        },
      ];
      const otherAdmins = state.users.filter(
        (u) => u.role === 'admin' && u.id !== user.id
      );
      for (const admin of otherAdmins) {
        notifs.push({
          message: `✅ کلاس "${req.title}" توسط ${user.name} تأیید شد.`,
          type: 'info',
          userId: admin.id,
        });
      }
      await sendBatchNotifications(notifs);

      await refreshData();
      await loadRoomeetData();
      showToast('✅ درخواست تأیید و کلاس در رومیت ایجاد شد', 'success');
    } catch (error) {
      await internalApi.updateRequest(id, {
        status: 'failed',
        errorMessage: error.message,
      });
      await refreshData();
      showToast(`❌ خطا در تأیید: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async (id) => {
    try {
      await internalApi.updateRequest(id, { status: 'rejected' });
      const req = state.requests.find((r) => r.id === id);
      if (req)
        await internalApi.addNotification(
          `❌ درخواست کلاس "${req.title}" رد شد`,
          'warning',
          req.teacherId
        );
      await refreshData();
      showToast('درخواست رد شد', 'info');
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  const handleEditRequest = (req) => {
    setEditingRequest(req);
    setEditRequestForm({ ...req });
    setShowEditRequestModal(true);
  };

  const handleEditRequestSubmit = async (e) => {
    e.preventDefault();
    if (!editingRequest) return;
    try {
      await internalApi.updateRequest(editingRequest.id, editRequestForm);
      if (editingRequest.status === 'approved') {
        const relatedCourse = state.courses.find(
          (c) =>
            c.teacherId === editingRequest.teacherId &&
            c.title === editingRequest.title &&
            c.date === editingRequest.date &&
            c.time === editingRequest.time
        );
        if (relatedCourse) {
          await internalApi.updateCourse(relatedCourse.id, {
            title: editRequestForm.title,
            grade: editRequestForm.grade,
            field: editRequestForm.field,
            subject: editRequestForm.subject,
            duration: editRequestForm.duration || relatedCourse.duration,
            date: editRequestForm.date,
            time: editRequestForm.time,
            price: editRequestForm.price,
            capacity: editRequestForm.capacity,
            description: editRequestForm.description,
          });
          showToast('کلاس مرتبط نیز به‌روزرسانی شد.', 'info');
        }
      }
      await refreshData();
      showToast('درخواست با موفقیت ویرایش شد', 'success');
      setShowEditRequestModal(false);
      setEditingRequest(null);
      setEditRequestForm(null);
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  // ============================================================
  // مدیریت کلاس‌ها — لغو
  // ============================================================
  const handleCancelCourse = async (courseId) => {
    const course = state.courses.find((c) => c.id === courseId);
    if (!course) {
      showToast('کلاس یافت نشد', 'error');
      return;
    }
    if (course.status === COURSE_STATUS.CANCELLED) {
      showToast('این کلاس قبلاً لغو شده است', 'info');
      return;
    }
    if (
      course.status === COURSE_STATUS.ENDED ||
      course.status === COURSE_STATUS.LIVE
    ) {
      showToast('امکان لغو کلاس‌های در حال برگزاری یا پایان‌یافته وجود ندارد', 'error');
      return;
    }
    const reason = prompt('دلیل لغو کلاس را وارد کنید (اختیاری):');
    if (reason === null) return;
    if (
      !window.confirm(
        'آیا از لغو این کلاس مطمئن هستید؟ وجه دانش‌آموزان برگشت داده خواهد شد.'
      )
    )
      return;
    try {
      await internalApi.cancelCourse(courseId, reason || 'بدون دلیل');
      // ✅ batch notifications
      const notifs = [
        {
          message: `❌ کلاس "${course.title}" توسط ادمین لغو شد.`,
          type: 'warning',
          userId: course.teacherId,
        },
      ];
      for (const s of (course.enrolledStudents || [])) {
        notifs.push({
          message: `❌ کلاس "${course.title}" لغو شد. دلیل: ${reason || 'بدون دلیل'}`,
          type: 'warning',
          userId: s.userId,
        });
      }
      await sendBatchNotifications(notifs);
      await refreshData();
      showToast('✅ کلاس لغو شد و وجه دانش‌آموزان بازگشت', 'success');
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  // ============================================================
  // ✅ deleteCourse — از متد یکپارچه InternalAPIService استفاده می‌کنه
  // (خودش refund + pendingAmount + endRoom + removeRoom رو مدیریت می‌کنه)
  // ============================================================
  const deleteCourse = async (courseId) => {
    if (
      !window.confirm(
        'آیا از حذف این کلاس مطمئن هستید؟ وجه دانش‌آموزان برگشت داده خواهد شد و اتاق از Roomeet هم پاک می‌شود.'
      )
    )
      return;
    try {
      const course = state.courses.find((c) => c.id === courseId);
      if (course) {
        const relatedRequest = state.requests.find(
          (r) =>
            r.title === course.title &&
            r.teacherId === course.teacherId &&
            r.date === course.date &&
            r.time === course.time
        );
        if (relatedRequest)
          await internalApi.updateRequest(relatedRequest.id, { status: 'deleted' });
      }
      await internalApi.deleteCourse(courseId);
      await refreshData();
      await loadRoomeetData();
      showToast('کلاس حذف شد و وجه دانش‌آموزان بازگشت', 'success');
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  const handleForceEnroll = async (userId, courseId) => {
    try {
      const result = await internalApi.enrollStudent(userId, courseId);
      showToast(result.message, result.success ? 'success' : 'error');
      if (result.success) await refreshData();
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  const openForceEnroll = (course) => {
    const enrolledIds = (course.enrolledStudents || []).map((s) => s.userId);
    const available = state.users.filter(
      (u) =>
        u.role === 'student' && u.active !== false && !enrolledIds.includes(u.id)
    );
    setForceEnrollCourse(course);
    setForceEnrollStudents(available);
    setForceEnrollModal(true);
  };

  const handleBulkNotification = async (e) => {
    e.preventDefault();
    if (!bulkNotificationData.message.trim()) {
      showToast('لطفاً متن پیام را وارد کنید', 'error');
      return;
    }
    setIsLoading(true);
    try {
      await internalApi.sendBulkNotification(
        bulkNotificationData.message,
        bulkNotificationData.type,
        bulkNotificationData.role || null
      );
      showToast('✅ نوتیفیکیشن ارسال شد', 'success');
      setBulkNotificationModal(false);
      setBulkNotificationData({ message: '', type: 'info', role: '' });
      await refreshData();
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

    // 🆕 تکمیل واریز برداشت با شماره پیگیری
  const handleCompleteWithdrawal = async (withdrawalId) => {
    const ref = window.prompt('شماره پیگیری واریز از بانک را وارد کنید:');
    if (!ref || !ref.trim()) {
      showToast('شماره پیگیری الزامی است', 'error');
      return;
    }
    try {
      const w = state.transactions.find((t) => t.id === withdrawalId);
      if (!w) return;

      await internalApi.updateTransaction(withdrawalId, {
        status: 'completed',
        adminRef: ref.trim(),
        completedAt: moment().format('jYYYY-jMM-jDD HH:mm'),
        completedBy: user.id,
      });

      // اطلاع به معلم
      await sendBatchNotifications([{
        message: `✅ درخواست برداشت ${w.amount.toLocaleString('fa-IR')} تومان شما واریز شد. کد پیگیری: ${ref.trim()}`,
        type: 'success',
        userId: w.userId,
      }]);

      await refreshData();
      showToast('✅ واریز تکمیل شد', 'success');
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error');
    }
  };

  // 🆕 رد درخواست برداشت و بازگشت وجه به کیف پول معلم
  const handleRejectWithdrawal = async (withdrawalId) => {
    if (!window.confirm('درخواست رد شود و مبلغ به کیف پول معلم بازگردد؟')) return;
    try {
      const w = state.transactions.find((t) => t.id === withdrawalId);
      if (!w) return;

      const teacher = state.users.find((u) => u.id === w.userId);
      if (!teacher) {
        showToast('معلم یافت نشد', 'error');
        return;
      }

      // بازگرداندن مبلغ به کیف پول
      await internalApi.updateUser(w.userId, {
        wallet: (teacher.wallet || 0) + w.amount,
      });

      // ثبت refund
      await internalApi.addTransaction({
        id: `rf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        userId: w.userId,
        amount: w.amount,
        date: moment().format('jYYYY-jMM-jDD'),
        type: 'refund',
        note: `بازگشت وجه درخواست برداشت رد شده (${w.id})`,
      });

      // به‌روزرسانی درخواست
      await internalApi.updateTransaction(withdrawalId, {
        status: 'rejected',
        rejectedAt: moment().format('jYYYY-jMM-jDD HH:mm'),
        rejectedBy: user.id,
      });

      await sendBatchNotifications([{
        message: `❌ درخواست برداشت ${w.amount.toLocaleString('fa-IR')} تومان شما رد شد. مبلغ به کیف پول شما بازگشت.`,
        type: 'warning',
        userId: w.userId,
      }]);

      await refreshData();
      showToast('درخواست رد شد و مبلغ به کیف پول معلم بازگشت', 'info');
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error');
    }
  };

  // ============================================================
  // ایجاد کلاس توسط ادمین
  // ============================================================
  const handleCreateRoom = async (e) => {
    e.preventDefault();
    if (!isRoomeetReady) {
      showToast('اتصال به رومیت برقرار نیست', 'error');
      return;
    }
    if (!selectedSaleId) {
      showToast('لطفاً یک بسته سرویس انتخاب کنید', 'error');
      return;
    }
    if (!roomForm.name.trim()) {
      showToast('لطفاً نام کلاس را وارد کنید', 'error');
      return;
    }
    if (!roomForm.date || !roomForm.time) {
      showToast('لطفاً تاریخ و ساعت شروع را انتخاب کنید', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const roomData = {
        name: roomForm.name,
        guest: roomForm.guest,
        welcomeMessage: roomForm.welcomeMessage,
        moderatorOnlyMessage: roomForm.moderatorOnlyMessage,
        startWithModerator: roomForm.startWithModerator,
        duration: roomForm.duration,
        language: roomForm.language,
        allowDownloadRecordings: roomForm.allowDownloadRecordings,
        webcamsOnlyForModerat: roomForm.webcamsOnlyForModerat,
        autoStartRecording: roomForm.autoStartRecording,
        muteOnStart: roomForm.muteOnStart,
        allowModsToEjectCameras: roomForm.allowModsToEjectCameras,
        disablePresentation: roomForm.disablePresentation,
        logoutURL: roomForm.logoutURL,
        lockSettingsDisablePrivateChat: roomForm.lockSettingsDisablePrivateChat,
        lockSettingsDisablePublicChat: roomForm.lockSettingsDisablePublicChat,
        lockSettingsHideUserList: roomForm.lockSettingsHideUserList,
        endWhenNoModeratorDelayInMinutes: roomForm.endWhenNoModeratorDelayInMinutes,
      };
      const result = await roomeetApi.createRoom(selectedSaleId, roomData);
      const theIdOfMeeting = result.theIdOfMeeting;

      let teacherMemberId = null;
      let teacherName = 'مدیر';
      if (roomForm.teacherId) {
        const teacher = state.users.find(
          (u) => u.id === roomForm.teacherId && u.role === 'teacher'
        );
        if (teacher) {
          teacherName = teacher.name;
          teacherMemberId = await internalApi.ensureUserInRoomeet(teacher.id);
          if (teacherMemberId) {
            await internalApi.ensureUserInRoom(teacher.id, theIdOfMeeting);
          }
        }
      }

      const newCourse = {
        id: Date.now(),
        title: roomForm.name,
        teacherId: roomForm.teacherId || '',
        teacher: teacherName,
        rating: 0,
        ratingCount: 0,
        price: Number(roomForm.price) || 0,
        grade: '',
        field: '',
        subject: '',
        date: roomForm.date,
        time: roomForm.time,
        duration: roomForm.duration || 60,
        capacity: Number(roomForm.capacity) || 0,
        enrolled: 0,
        icon: 'fa-chalkboard',
        color: '#0b2b5c',
        sponsored: false,
        popular: false,
        status: COURSE_STATUS.SCHEDULED,
        siroomehClassId: theIdOfMeeting,
        siroomehLink: `https://roomeet.ir/join/${theIdOfMeeting}`,
        siroomehSettings: roomData,
        enrolledStudents: [],
        teacherRoomeetMemberId: teacherMemberId || '',
        roomIsLive: false,
        roomOfflineSince: null,
        refundProcessed: false,
      };
      await internalApi.addCourse(newCourse);
      dispatch({ type: 'ADD_COURSE', payload: newCourse });

      if (roomForm.teacherId) {
        await internalApi.addRequest({
          title: roomForm.name,
          teacherId: roomForm.teacherId,
          date: roomForm.date,
          time: roomForm.time,
          duration: roomForm.duration || 60,
          price: 0,
          capacity: 0,
          grade: '',
          field: '',
          subject: '',
          description: 'کلاس ایجاد شده توسط ادمین',
          status: 'approved',
          courseId: newCourse.id,
          createdBy: 'admin',
        });
      }

      showToast('✅ کلاس ایجاد شد (برنامه‌ریزی شده)', 'success');
      setRoomForm({ ...DEFAULT_ROOM_FORM });
      setShowAdvancedOptions(false);
      setRoomModal(false);
      await refreshData();
      await loadRoomeetData();
    } catch (e) {
      showToast(`❌ خطا: ${e.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // ✅ C27: ویرایش کلاس + lastUpdatedAt check
  // ============================================================
  const handleUpdateRoom = async (e) => {
    e.preventDefault();
    if (!editingRoom) return;
    setIsLoading(true);
    try {
      const roomId = getRoomId(editingRoom);
      // ✅ C27: check for concurrent edit
      const relatedCourse = state.courses.find((c) => c.siroomehClassId === roomId);
      if (relatedCourse && editingRoom._loadedAt && relatedCourse.lastUpdatedAt) {
        if (relatedCourse.lastUpdatedAt !== editingRoom._loadedAt) {
          if (!window.confirm(
            'این کلاس توسط ادمین دیگری ویرایش شده است. آیا می‌خواهید نسخه‌ی خود را ذخیره کنید (اطلاعات دیگران از دست می‌رود)؟'
          )) {
            setShowEditRoomModal(false);
            setEditingRoom(null);
            return;
          }
        }
      }

      const { saleId, date, time, ...rest } = editRoomForm;
      const updateData = {
        name: rest.name,
        guest: rest.guest,
        welcomeMessage: rest.welcomeMessage,
        moderatorOnlyMessage: rest.moderatorOnlyMessage,
        startWithModerator: rest.startWithModerator,
        duration: rest.duration,
        language: rest.language,
        allowDownloadRecordings: rest.allowDownloadRecordings,
        webcamsOnlyForModerat: rest.webcamsOnlyForModerat,
        autoStartRecording: rest.autoStartRecording,
        muteOnStart: rest.muteOnStart,
        allowModsToEjectCameras: rest.allowModsToEjectCameras,
        disablePresentation: rest.disablePresentation,
        logoutURL: rest.logoutURL,
        lockSettingsDisablePrivateChat: rest.lockSettingsDisablePrivateChat,
        lockSettingsDisablePublicChat: rest.lockSettingsDisablePublicChat,
        lockSettingsHideUserList: rest.lockSettingsHideUserList,
        endWhenNoModeratorDelayInMinutes: rest.endWhenNoModeratorDelayInMinutes,
      };
      await roomeetApi.updateRoom(roomId, saleId, updateData);

      if (relatedCourse) {
        await internalApi.updateCourse(relatedCourse.id, {
          date: date || relatedCourse.date,
          time: time || relatedCourse.time,
          title: rest.name,
          duration: rest.duration,
          siroomehSettings: updateData,
          lastUpdatedAt: Date.now(),
        });
        const req = state.requests.find(
          (r) =>
            r.teacherId === relatedCourse.teacherId &&
            r.title === relatedCourse.title &&
            r.date === relatedCourse.date &&
            r.time === relatedCourse.time
        );
        if (req) {
          await internalApi.updateRequest(req.id, {
            title: rest.name,
            date: date || relatedCourse.date,
            time: time || relatedCourse.time,
          });
        }
        await refreshData();
      }

      showToast('✅ کلاس ویرایش شد', 'success');
      setShowEditRoomModal(false);
      setEditingRoom(null);
      await loadRoomeetData();
    } catch (error) {
      showToast(`❌ خطا: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // شروع کلاس توسط ادمین
  // ============================================================
  const handleAdminStartCourse = async (courseId) => {
    if (isStartingRef.current) return;
    const course = state.courses.find((c) => c.id === courseId);
    if (!course) {
      showToast('کلاس یافت نشد', 'error');
      return;
    }
    if (!canStartCourse(course, moment())) {
      showToast('هنوز زمان شروع کلاس نرسیده است', 'warning');
      return;
    }
    if (!window.confirm('آیا از شروع این کلاس مطمئن هستید؟')) return;
    isStartingRef.current = true;

    const newTab = window.open('about:blank', '_blank');

    try {
      await internalApi.startCourse(courseId, 'admin');

      const join = await internalApi.joinCourse(courseId);
      if (join?.link) {
        if (newTab && !newTab.closed) newTab.location.href = join.link;
        else window.open(join.link, '_blank');
      }

      await refreshData();
      await loadRoomeetData();
      showToast('✅ کلاس شروع شد', 'success');
    } catch (error) {
      if (newTab && !newTab.closed) newTab.close();
      showToast(`❌ خطا در شروع: ${error.message}`, 'error');
    } finally {
      isStartingRef.current = false;
    }
  };

  const handleAdminJoinCourse = async (courseId) => {
    try {
      const join = await internalApi.joinCourse(courseId);
      if (join?.link) window.open(join.link, '_blank');
    } catch (error) {
      showToast(`❌ خطا در ورود: ${error.message}`, 'error');
    }
  };

  const handleAdminEndCourse = async (courseId) => {
    if (
      !window.confirm(
        'آیا از اتمام این کلاس مطمئن هستید؟ کلاس برای همه بسته می‌شود.'
      )
    )
      return;
    try {
      await internalApi.endCourse(courseId, 'admin', 'manual');
      await refreshData();
      await loadRoomeetData();
      showToast('✅ کلاس پایان یافت', 'success');
    } catch (error) {
      showToast(`❌ خطا: ${error.message}`, 'error');
    }
  };

  const handleDeleteRoomOnly = async (theIdOfMeeting) => {
    const liveRoom = onlineRooms.find((r) => String(getRoomId(r)) === theIdOfMeeting);
    if (liveRoom) {
      showToast('این اتاق در حال برگزاری است. ابتدا پایان دهید.', 'warning');
      return;
    }
    if (!window.confirm('آیا از حذف این اتاق از Roomeet مطمئن هستید؟')) return;

    try {
      try {
        await roomeetApi.endRoom(theIdOfMeeting);
        await new Promise((r) => setTimeout(r, 1500));
      } catch (_) {}
      await roomeetApi.removeRoom(theIdOfMeeting);
      showToast('✅ اتاق از Roomeet حذف شد', 'success');
      await loadRoomeetData();
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error');
    }
  };

  // ============================================================
  // ضبط‌ها
  // ============================================================
  const handleViewRecordings = async (theIdOfMeeting) => {
    try {
      const recs = await roomeetApi.getRoomRecordings(theIdOfMeeting);
      setRecordings(recs.recordings || []);
      setSelectedRoomForRecording(theIdOfMeeting);
      setRecordingsModal(true);
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error');
    }
  };

  const handlePublishRecording = async (recordId, publish) => {
    try {
      await roomeetApi.publishRecording(recordId, publish);
      showToast(publish ? 'ضبط منتشر شد' : 'انتشار لغو شد', 'success');
      if (selectedRoomForRecording) {
        const recs = await roomeetApi.getRoomRecordings(selectedRoomForRecording);
        setRecordings(recs.recordings || []);
      }
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error');
    }
  };

  const handleDeleteRecording = async (recordId, serverId) => {
    if (!window.confirm('آیا از حذف ضبط مطمئن هستید؟')) return;
    try {
      await roomeetApi.deleteRecording(recordId, serverId);
      showToast('ضبط حذف شد', 'success');
      if (selectedRoomForRecording) {
        const recs = await roomeetApi.getRoomRecordings(selectedRoomForRecording);
        setRecordings(recs.recordings || []);
      }
    } catch (e) {
      showToast(`خطا: ${e.message}`, 'error');
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      await internalApi.updateAdminSettings(settings);
      dispatch({ type: 'UPDATE_SETTINGS', payload: settings });
      await refreshData();
      showToast('تنظیمات ذخیره شد', 'success');
    } catch (error) {
      showToast(`خطا: ${error.message}`, 'error');
    }
  };

  const handleEditRoom = (room) => {
    const roomId = getRoomId(room);
    const relatedCourse = state.courses.find((c) => c.siroomehClassId === roomId);
    setEditingRoom({ ...room, _loadedAt: relatedCourse?.lastUpdatedAt || null });
    setEditRoomForm({
      name: room.name || '',
      guest: room.guest !== undefined ? room.guest : true,
      welcomeMessage: room.welcomeMessage || '',
      moderatorOnlyMessage: room.moderatorOnlyMessage || '',
      startWithModerator: room.startWithModerator || false,
      duration: room.duration || 60,
      language: room.language || 'fa',
      allowDownloadRecordings:
        room.allowDownloadRecordings !== undefined ? room.allowDownloadRecordings : true,
      webcamsOnlyForModerat:
        room.webcamsOnlyForModerat !== undefined ? room.webcamsOnlyForModerat : true,
      autoStartRecording: room.autoStartRecording || false,
      muteOnStart: room.muteOnStart || false,
      allowModsToEjectCameras:
        room.allowModsToEjectCameras !== undefined
          ? room.allowModsToEjectCameras
          : true,
      disablePresentation: room.disablePresentation || false,
      logoutURL: room.logoutURL || '',
      lockSettingsDisablePrivateChat: room.lockSettingsDisablePrivateChat || false,
      lockSettingsDisablePublicChat: room.lockSettingsDisablePublicChat || false,
      lockSettingsHideUserList: room.lockSettingsHideUserList || false,
      endWhenNoModeratorDelayInMinutes:
        room.endWhenNoModeratorDelayInMinutes || 15,
      saleId: selectedSaleId || settings.defaultSaleId || services[0]?.saleId || '',
      date: relatedCourse?.date || '',
      time: relatedCourse?.time || '',
    });
    setShowEditRoomModal(true);
  };

  const filteredTransactions = useMemo(() => {
    let result = transactions;
    if (transactionFilters.type)
      result = result.filter((t) => t.type === transactionFilters.type);

    // 🆕 تبدیل تاریخ میلادی input به شمسی برای مقایسه با t.date
    const dateFromJalali = transactionFilters.dateFrom
      ? moment(transactionFilters.dateFrom, 'YYYY-MM-DD').format('jYYYY-jMM-jDD')
      : '';
    const dateToJalali = transactionFilters.dateTo
      ? moment(transactionFilters.dateTo, 'YYYY-MM-DD').format('jYYYY-jMM-jDD')
      : '';

    if (dateFromJalali)
      result = result.filter((t) => t.date >= dateFromJalali);
    if (dateToJalali)
      result = result.filter((t) => t.date <= dateToJalali);
    return result;
  }, [transactions, transactionFilters]);

  const tabs = [
    { key: 'dashboard', icon: 'fa-th-large', label: 'خلاصه' },
    { key: 'users', icon: 'fa-users', label: 'کاربران' },
    { key: 'review-requests', icon: 'fa-clipboard-list', label: 'بررسی درخواست‌ها' },
    { key: 'courses-management', icon: 'fa-list', label: 'مدیریت کلاس‌ها' },
    { key: 'rooms', icon: 'fa-video', label: 'کلاس‌های رومیت' },
    { key: 'recordings', icon: 'fa-record-vinyl', label: 'ضبط‌ها' },
    { key: 'reports', icon: 'fa-chart-pie', label: 'گزارشات' },
    { key: 'withdrawals', icon: 'fa-money-check-alt', label: 'درخواست‌های برداشت' }, // 🆕
    { key: 'transactions', icon: 'fa-credit-card', label: 'تراکنش‌ها' },
    { key: 'settings', icon: 'fa-cog', label: 'تنظیمات' },
  ];

  const renderCourseActions = (c) => {
    const startable = canStartCourse(c, nowMoment);

    if (c.status === COURSE_STATUS.CANCELLED) {
      return (
        <button
          className="icon-btn danger"
          onClick={() => deleteCourse(c.id)}
          title="حذف"
        >
          <i className="fas fa-trash" />
        </button>
      );
    }

    if (c.status === COURSE_STATUS.ENDED) {
      return (
        <button
          className="icon-btn danger"
          onClick={() => deleteCourse(c.id)}
          title="حذف"
        >
          <i className="fas fa-trash" />
        </button>
      );
    }

    if (c.status === COURSE_STATUS.LIVE) {
      return (
        <>
          <button
            className="icon-btn primary"
            onClick={() => handleAdminJoinCourse(c.id)}
            title="ورود به عنوان مدیر"
          >
            <i className="fas fa-sign-in-alt" />
          </button>
          <button
            className="icon-btn danger"
            onClick={() => handleAdminEndCourse(c.id)}
            title="پایان کلاس"
          >
            <i className="fas fa-stop" />
          </button>
        </>
      );
    }

    return (
      <>
        {startable && (
          <button
            className="icon-btn success"
            onClick={() => handleAdminStartCourse(c.id)}
            title="شروع کلاس"
          >
            <i className="fas fa-play" />
          </button>
        )}
        <button
          className="icon-btn warning"
          onClick={() => handleCancelCourse(c.id)}
          title="لغو کلاس"
        >
          <i className="fas fa-ban" />
        </button>
        <button
          className="icon-btn danger"
          onClick={() => deleteCourse(c.id)}
          title="حذف"
        >
          <i className="fas fa-trash" />
        </button>
      </>
    );
  };

  // ============================================================
  // رندر محتوا
  // ============================================================
  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <div className="tab-dashboard">
            <div className="stats-grid">
              <div className="stat-card glass pc-animate-slide-up">
                <div className="stat-icon"><i className="fas fa-video" /></div>
                <div className="stat-content">
                  <div className="label">کلاس‌های فعال</div>
                  <div className="value">
                    {courses.filter(
                      (c) =>
                        c.status === COURSE_STATUS.SCHEDULED ||
                        c.status === COURSE_STATUS.LIVE
                    ).length}
                  </div>
                </div>
              </div>
              <div className="stat-card glass pc-animate-slide-up pc-delay-1">
                <div className="stat-icon"><i className="fas fa-clock" /></div>
                <div className="stat-content">
                  <div className="label">درخواست‌های جدید</div>
                  <div className="value">{pendingRequests.length}</div>
                </div>
              </div>
              <div className="stat-card glass pc-animate-slide-up pc-delay-2">
                <div className="stat-icon"><i className="fas fa-user-tie" /></div>
                <div className="stat-content">
                  <div className="label">معلمان</div>
                  <div className="value">
                    {users.filter((u) => u.role === 'teacher').length}
                  </div>
                </div>
              </div>
              <div className="stat-card glass pc-animate-slide-up pc-delay-3">
  <div className="stat-icon"><i className="fas fa-coins" /></div>
  <div className="stat-content">
    <div className="label">درآمد واقعی سکو</div>
    <div className="value">
      {transactions
        .filter((t) => ['commission', 'vat', 'cancel_penalty'].includes(t.type))
        .reduce((acc, t) => acc + (t.amount || 0), 0)
        .toLocaleString('fa-IR')}{' '}
      تومان
    </div>
    <div
      style={{
        fontSize: '0.68rem',
        color: 'var(--pc-text-muted)',
        marginTop: '0.25rem',
      }}
    >
      کارمزد: {transactions.filter((t) => t.type === 'commission').reduce((a, t) => a + (t.amount || 0), 0).toLocaleString('fa-IR')}
      {' | '}
      مالیات: {transactions.filter((t) => t.type === 'vat').reduce((a, t) => a + (t.amount || 0), 0).toLocaleString('fa-IR')}
      {' | '}
      جریمه: {transactions.filter((t) => t.type === 'cancel_penalty').reduce((a, t) => a + (t.amount || 0), 0).toLocaleString('fa-IR')}
    </div>
  </div>
</div>
            </div>

            <div className="dashboard-grid">
              <div className="card glass pc-animate-fade-in">
                <div className="card-header">
                  <h3><i className="fas fa-chart-line" /> خلاصه عملکرد</h3>
                </div>
                <div className="summary-grid">
                  <div>
                    <span>کل کلاس‌ها</span>
                    <br />
                    <strong>{courses.length}</strong>
                  </div>
                  <div>
                    <span>کلاس‌های برگزارشده</span>
                    <br />
                    <strong>
                      {courses.filter((c) => c.status === COURSE_STATUS.ENDED).length}
                    </strong>
                  </div>
                  <div>
                    <span>دانش‌آموزان</span>
                    <br />
                    <strong>{users.filter((u) => u.role === 'student').length}</strong>
                  </div>
                  <div>
                    <span>کلاس‌های لغو شده</span>
                    <br />
                    <strong>
                      {courses.filter((c) => c.status === COURSE_STATUS.CANCELLED).length}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="card glass pc-animate-fade-in pc-delay-1">
                <div className="card-header">
                  <h3><i className="fas fa-exclamation-triangle" /> نیاز به بررسی</h3>
                </div>
                <ul className="alert-list">
                  <li>
                    <i className="fas fa-clock" style={{ color: 'var(--pc-gold)' }} />{' '}
                    {pendingRequests.length} درخواست جدید
                  </li>
                  <li>
                    <i
                      className="fas fa-exclamation-triangle"
                      style={{ color: 'var(--pc-danger)' }}
                    />{' '}
                    {state.notifications.filter((n) => n.type === 'warning').length} هشدار
                  </li>
                  <li>
                    <i
                      className="fas fa-credit-card"
                      style={{ color: 'var(--pc-success)' }}
                    />{' '}
                    {transactions.filter((t) => t.type === 'withdraw').length} درخواست برداشت
                  </li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'users':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-users" /> مدیریت کاربران</h3>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  className="btn-outline btn-sm"
                  onClick={handleFullSync}
                  disabled={isSyncing || isLoading}
                >
                  <i className={`fas ${isSyncing ? 'fa-spinner fa-spin' : 'fa-sync'}`} />{' '}
                  {isSyncing ? 'در حال سینک...' : 'همگام‌سازی کامل با Roomeet'}
                </button>
                <button
                  className="btn-primary btn-sm"
                  onClick={() => setAddUserModal(true)}
                >
                  <i className="fas fa-plus" /> افزودن کاربر
                </button>
              </div>
            </div>
            <div className="user-list pc-stagger-enter">
              {paginatedUsers.map((u) => (
                <div key={u.id} className="user-item pc-animate-slide-up">
                  <div className="user-info">
                    <img
                      src={`https://ui-avatars.com/api/?name=${(u.name || 'Unknown').replace(/ /g, '+')}&background=0b2b5c&color=fff&size=40`}
                      alt={u.name}
                      loading="lazy"
                    />
                    <div>
                      <strong>{u.name || 'بدون نام'}</strong>
                      <br />
                      <span className="email">
                        <i className="fas fa-envelope" /> {u.email || 'بدون ایمیل'}
                      </span>
                      {u.roomeetMemberId && (
                        <span
                          style={{ fontSize: '0.7rem', color: '#34C759', marginRight: '0.5rem' }}
                          title={`Roomeet ID: ${u.roomeetMemberId}`}
                        >
                          ✓ Roomeet
                        </span>
                      )}
                    </div>
                  </div>
                  <span className={`role-tag ${u.role}`}>
                    {u.role === 'teacher' ? '👨‍🏫 معلم'
                      : u.role === 'admin' ? '👑 ادمین'
                      : '🧑‍🎓 دانش‌آموز'}
                  </span>
                  <span className={`status-tag ${u.active !== false ? 'active' : 'inactive'}`}>
                    {u.active !== false ? 'فعال' : 'غیرفعال'}
                  </span>
                  <div className="user-actions">
                    <button
                      className="icon-btn"
                      onClick={() => { setSelectedUser(u); setModalOpen(true); }}
                    >
                      <i className="fas fa-eye" />
                    </button>
                    <button className="icon-btn" onClick={() => handleEditUser(u)}>
                      <i className="fas fa-edit" />
                    </button>
                    <button className="icon-btn" onClick={() => toggleUserStatus(u.id)}>
                      <i className={`fas ${u.active !== false ? 'fa-toggle-on' : 'fa-toggle-off'}`} />
                    </button>
                    {u.role !== 'admin' && (
                      <button
                        className="icon-btn danger"
                        onClick={() => handleDeleteUser(u.id)}
                      >
                        <i className="fas fa-trash" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {totalUserPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <i className="fas fa-chevron-right" />
                </button>
                <span>صفحه {page} از {totalUserPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalUserPages, p + 1))}
                  disabled={page === totalUserPages}
                >
                  <i className="fas fa-chevron-left" />
                </button>
              </div>
            )}
          </div>
        );

      case 'review-requests': {
        const allPending = [...pendingRequests, ...failedRequests];
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-clipboard-list" /> درخواست‌های تأیید</h3>
            </div>
            <div className="request-list pc-stagger-enter">
              {allPending.length === 0 ? (
                <p className="empty-state">هیچ درخواست جدیدی وجود ندارد</p>
              ) : (
                allPending.map((req) => (
                  <div key={req.id} className="request-item pc-animate-slide-up">
                    <div className="request-info">
                      <h4>{req.title}</h4>
                      <p>
                        <i className="fas fa-user" />{' '}
                        {state.users.find((u) => u.id === req.teacherId)?.name || 'نامشخص'}{' '}
                        – <i className="fas fa-calendar-alt" /> {req.date} – {req.time}
                      </p>
                      <p>
                        <i className="fas fa-tag" />{' '}
                        {(req.price || 0).toLocaleString('fa-IR')} تومان – ظرفیت {req.capacity}
                      </p>
                      {req.errorMessage && (
                        <p style={{ color: '#f43f5e', fontSize: '0.85rem' }}>
                          <i className="fas fa-exclamation-triangle" /> {req.errorMessage}
                        </p>
                      )}
                    </div>
                    <span className={`status-badge ${req.status}`}>
                      {req.status === 'pending' ? '⏳ در انتظار' : '❌ ناموفق'}
                    </span>
                    <div className="request-actions">
                      <button
                        className="icon-btn primary"
                        onClick={() => handleEditRequest(req)}
                      >
                        <i className="fas fa-edit" title="ویرایش" />
                      </button>
                      {req.status === 'pending' ? (
                        <>
                          <button
                            className="icon-btn success"
                            onClick={() => handleApprove(req.id)}
                            disabled={isLoading || !isRoomeetReady}
                          >
                            <i className="fas fa-check" title="تأیید" />
                          </button>
                          <button
                            className="icon-btn danger"
                            onClick={() => handleReject(req.id)}
                            disabled={isLoading}
                          >
                            <i className="fas fa-times" title="رد" />
                          </button>
                        </>
                      ) : (
                        <button
                          className="icon-btn warning"
                          onClick={() => handleApprove(req.id, true)}
                          disabled={isLoading || !isRoomeetReady}
                        >
                          <i className="fas fa-redo" title="تلاش مجدد" />
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      }

      case 'courses-management': {
        // ✅ مشکل ۱: منطق فیلتر تمیز و بدون باگ
        let filteredCourses = [];
        if (filterStatus === 'all') {
          filteredCourses = courses;
        } else if (filterStatus === 'scheduled') {
          // scheduled + live
          filteredCourses = courses.filter(
            (c) =>
              c.status === COURSE_STATUS.SCHEDULED ||
              c.status === COURSE_STATUS.LIVE
          );
        } else if (filterStatus === 'scheduled-only') {
          filteredCourses = courses.filter(
            (c) => c.status === COURSE_STATUS.SCHEDULED
          );
        } else if (filterStatus === 'live') {
          filteredCourses = courses.filter(
            (c) => c.status === COURSE_STATUS.LIVE
          );
        } else {
          filteredCourses = courses.filter((c) => c.status === filterStatus);
        }

        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-list" /> مدیریت کلاس‌ها</h3>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="form-control"
                  style={{ width: 'auto' }}
                >
                  <option value="scheduled">همه‌ی فعال (برنامه‌ریزی + در حال برگزاری)</option>
                  <option value="scheduled-only">فقط برنامه‌ریزی شده</option>
                  <option value="live">فقط در حال برگزاری</option>
                  <option value="ended">پایان‌یافته</option>
                  <option value="cancelled">لغو شده</option>
                  <option value="all">همه</option>
                </select>
                <button
                  className="btn-primary btn-sm"
                  onClick={() => setBulkNotificationModal(true)}
                >
                  <i className="fas fa-bullhorn" /> ارسال نوتیفیکیشن جمعی
                </button>
              </div>
            </div>
            {filteredCourses.length === 0 ? (
              <p className="empty-state">هیچ کلاسی با این وضعیت یافت نشد</p>
            ) : (
              <table className="transaction-table">
                <thead>
                  <tr>
                    <th>عنوان</th>
                    <th>معلم</th>
                    <th>تاریخ</th>
                    <th>ساعت</th>
                    <th>مدت</th>
                    <th>قیمت</th>
                    <th>وضعیت</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCourses
                    .slice()
                    .reverse()
                    .map((c) => (
                      <tr key={c.id}>
                        <td>{c.title}</td>
                        <td>{c.teacher}</td>
                        <td>{c.date}</td>
                        <td>{c.time}</td>
                        <td>{c.duration || 60} دقیقه</td>
                        <td>{(c.price || 0).toLocaleString('fa-IR')} تومان</td>
                        <td>{getCourseStatusLabel(c, nowMoment)}</td>
                        <td>
                          <button
                            className="icon-btn"
                            onClick={() => {
                              setSelectedCourseStudentsAdmin(c.enrolledStudents || []);
                              setSelectedCourseTitleAdmin(c.title);
                              setShowStudentsModalAdmin(true);
                            }}
                            title={`${c.enrolled} دانش‌آموز`}
                          >
                            <i className="fas fa-users" />
                          </button>
                          <button
                            className="icon-btn primary"
                            onClick={() => openForceEnroll(c)}
                            title="ثبت‌نام اجباری"
                          >
                            <i className="fas fa-user-plus" />
                          </button>
                          {renderCourseActions(c)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        );
      }

      case 'rooms':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-video" /> کلاس‌های رومیت</h3>
              <button className="btn-primary btn-sm" onClick={() => setRoomModal(true)}>
                <i className="fas fa-plus" /> ایجاد کلاس
              </button>
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ flex: 1, minWidth: '200px' }}>
                <input
                  type="text"
                  placeholder="جستجوی کلاس‌ها..."
                  value={roomSearchKeyword}
                  onChange={(e) => setRoomSearchKeyword(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <button
                className="btn-primary btn-sm"
                onClick={async () => {
                  try {
                    const rooms = await roomeetApi.getAllRooms(1, 100, roomSearchKeyword);
                    setAllRooms(rooms.meetings || []);
                  } catch (error) {
                    showToast(`خطا: ${error.message}`, 'error');
                  }
                }}
              >
                <i className="fas fa-search" /> جستجو
              </button>
            </div>

            {allRooms.length === 0 ? (
              <p className="empty-state">هیچ کلاسی در رومیت یافت نشد</p>
            ) : (
              <table className="transaction-table">
                <thead>
                  <tr>
                    <th>نام</th>
                    <th>دوره متصل</th>
                    <th>وضعیت</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
  {allRooms.map((r) => {
    const roomId = getRoomId(r);
    // ✅ مقایسه‌ی String برای جلوگیری از type mismatch
    const relatedCourse = state.courses.find(
      (c) => String(c.siroomehClassId).trim() === String(roomId).trim()
    );
    const isLive = relatedCourse
      ? relatedCourse.status === COURSE_STATUS.LIVE
      : false;
    const isScheduled =
      relatedCourse?.status === COURSE_STATUS.SCHEDULED;
    const startable = relatedCourse
      ? canStartCourse(relatedCourse, nowMoment)
      : false;

    return (
      <tr key={roomId || r.name}>
        <td>{r.name}</td>
        <td>
          {relatedCourse
            ? `${relatedCourse.title} (${relatedCourse.date} ${relatedCourse.time})`
            : '— بدون دوره —'}
        </td>
        <td>{isLive ? '🟢 در حال برگزاری' : '⚪ غیرفعال'}</td>
        <td>
          {/* ویرایش */}
          <button
            className="icon-btn"
            onClick={() => handleEditRoom(r)}
            title="ویرایش"
          >
            <i className="fas fa-edit" />
          </button>

          {/* ✅ اگه کلاس متصل هست، دکمه‌های شروع/ورود/پایان */}
          {relatedCourse ? (
            <>
              {/* شروع کلاس (فقط scheduled) */}
              {isScheduled && (
                <button
                  className={`icon-btn ${startable ? 'success' : ''}`}
                  onClick={() => handleAdminStartCourse(relatedCourse.id)}
                  disabled={!startable}
                  title={
                    startable
                      ? 'شروع کلاس'
                      : 'هنوز زمان شروع نرسیده (یا از ۳ ساعت گذشته)'
                  }
                >
                  <i className="fas fa-play" />
                </button>
              )}

              {/* ورود به عنوان مدیر (فقط live) */}
              {isLive && (
                <button
                  className="icon-btn primary"
                  onClick={() => handleAdminJoinCourse(relatedCourse.id)}
                  title="ورود به عنوان مدیر"
                >
                  <i className="fas fa-sign-in-alt" />
                </button>
              )}

              {/* اتمام (live) یا لغو (scheduled) */}
              {isLive && (
                <button
                  className="icon-btn danger"
                  onClick={() => handleAdminEndCourse(relatedCourse.id)}
                  title="اتمام کلاس"
                >
                  <i className="fas fa-stop" />
                </button>
              )}
              {isScheduled && (
                <button
                  className="icon-btn warning"
                  onClick={() => handleCancelCourse(relatedCourse.id)}
                  title="لغو کلاس"
                >
                  <i className="fas fa-ban" />
                </button>
              )}
            </>
          ) : (
            /* اگه اتاق به کلاسی متصل نیست، فقط حذف */
            <button
              className="icon-btn danger"
              onClick={() => handleDeleteRoomOnly(roomId)}
              title="حذف اتاق از Roomeet"
            >
              <i className="fas fa-trash" />
            </button>
          )}

          {/* ضبط‌ها */}
          <button
            className="icon-btn"
            onClick={() => handleViewRecordings(roomId)}
            title="ضبط‌ها"
          >
            <i className="fas fa-list" />
          </button>
        </td>
      </tr>
    );
  })}
</tbody>
              </table>
            )}
          </div>
        );

      case 'recordings':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-record-vinyl" /> مدیریت ضبط‌ها</h3>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>انتخاب کلاس</label>
                <select
                  value={selectedRoomForRecording}
                  onChange={async (e) => {
                    const id = e.target.value;
                    setSelectedRoomForRecording(id);
                    if (id) {
                      try {
                        const recs = await roomeetApi.getRoomRecordings(id);
                        setRecordings(recs.recordings || []);
                        setRecordingsModal(true);
                      } catch (error) {
                        showToast(`خطا: ${error.message}`, 'error');
                      }
                    }
                  }}
                >
                  <option value="">انتخاب کلاس...</option>
                  {allRooms.map((r) => {
                    const rid = getRoomId(r);
                    return (
                      <option key={rid} value={rid}>
                        {r.name} ({rid})
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <Modal
              isOpen={recordingsModal}
              onClose={() => setRecordingsModal(false)}
              title="ضبط‌های کلاس"
            >
              {recordings.length === 0 ? (
                <p className="empty-state">هیچ ضبطی یافت نشد</p>
              ) : (
                recordings.map((r) => {
                  // ✅ D43: platform soft check
                  const platform = String(r.platform || 'bbb').toLowerCase();
                  const isBbb = platform === 'bbb';
                  return (
                    <div
                      key={r.recordId}
                      style={{
                        padding: '0.8rem 0',
                        borderBottom: '1px solid var(--pc-border-color)',
                      }}
                    >
                      <div>
                        <strong>{r.startTime}</strong> – مدت: {r.duration}
                        {r.link && (
                          <a
                            href={r.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ marginRight: '1rem' }}
                          >
                            مشاهده
                          </a>
                        )}
                      </div>
                      <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {r.downloadLink && (
                          <a
                            href={r.downloadLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary btn-sm"
                          >
                            دانلود
                          </a>
                        )}
                        {isBbb && (
                          <>
                            <button
                              className="btn-outline btn-sm"
                              onClick={() => handlePublishRecording(r.recordId, !r.isPublished)}
                            >
                              {r.isPublished ? 'لغو انتشار' : 'انتشار'}
                            </button>
                            <button
                              className="btn-outline btn-sm danger"
                              onClick={() => handleDeleteRecording(r.recordId, r.serverId)}
                            >
                              حذف
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </Modal>
          </div>
        );

      case 'reports':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-chart-pie" /> گزارشات جامع</h3>
            </div>
            <div className="report-grid">
              <div className="report-item">
                <div className="report-icon"><i className="fas fa-star" /></div>
                <p>محبوب‌ترین درس</p>
                <strong>
                  {(() => {
                    const counts = courses.reduce((acc, c) => {
                      acc[c.subject] = (acc[c.subject] || 0) + 1;
                      return acc;
                    }, {});
                    const entry = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
                    return entry ? entry[0] : '-';
                  })()}
                </strong>
              </div>
              <div className="report-item">
                <div className="report-icon"><i className="fas fa-trophy" /></div>
                <p>پربازده‌ترین معلم</p>
                <strong>
                  {users
                    .filter((u) => u.role === 'teacher')
                    .sort((a, b) => (b.wallet || 0) - (a.wallet || 0))[0]?.name || '-'}
                </strong>
              </div>
              <div className="report-item">
                <div className="report-icon"><i className="fas fa-clock" /></div>
                <p>زمان شلوغ</p>
                <strong>
                  {(() => {
                    const counts = courses.map((c) => c.time).reduce((acc, t) => {
                      acc[t] = (acc[t] || 0) + 1;
                      return acc;
                    }, {});
                    const entry = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
                    return entry ? entry[0] : '-';
                  })()}
                </strong>
              </div>
              <div className="report-item">
                <div className="report-icon"><i className="fas fa-chart-bar" /></div>
                <p>میانگین قیمت (تومان)</p>
                <strong>
                  {(
                    courses.reduce((acc, c) => acc + (c.price || 0), 0) /
                    (courses.length || 1)
                  ).toLocaleString('fa-IR')}
                </strong>
              </div>
              <div className="report-item">
                <div className="report-icon"><i className="fas fa-calendar-alt" /></div>
                <p>کلاس‌های برگزارشده</p>
                <strong>
                  {courses.filter((c) => c.status === COURSE_STATUS.ENDED).length}
                </strong>
              </div>
              <div className="report-item">
                <div className="report-icon"><i className="fas fa-times-circle" /></div>
                <p>کلاس‌های لغو شده</p>
                <strong>
                  {courses.filter((c) => c.status === COURSE_STATUS.CANCELLED).length}
                </strong>
              </div>
            </div>
          </div>
        );



              case 'withdrawals': {
        const pendingWithdrawals = state.transactions
          .filter((t) => t.type === 'withdraw' && t.status === 'pending')
          .slice()
          .reverse();

        const allWithdrawals = state.transactions
          .filter((t) => t.type === 'withdraw' && t.status !== 'pending')
          .slice()
          .reverse()
          .slice(0, 30);

        const renderRow = (w, showActions) => {
          const teacher = state.users.find((u) => u.id === w.userId);
          return (
            <tr key={w.id}>
              <td>
                <div style={{ fontWeight: 'bold' }}>{teacher?.name || '—'}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{teacher?.phone || ''}</div>
              </td>
              <td style={{ fontSize: '0.85rem' }}>{w.fullName || '—'}</td>
              <td dir="ltr" style={{ fontFamily: 'monospace', fontSize: '0.85rem', letterSpacing: '1px' }}>
                {w.cardNumber ? w.cardNumber.replace(/(.{4})/g, '$1 ') : '—'}
              </td>
              <td dir="ltr" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                {w.shaba || '—'}
              </td>
              <td style={{ fontWeight: 'bold', color: '#1f2937' }}>
                {w.amount.toLocaleString('fa-IR')} تومان
              </td>
              <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>{w.date || '—'}</td>
              <td>
                {showActions ? (
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button
                      className="btn-primary btn-sm"
                      style={{ padding: '0.4rem 0.7rem', fontSize: '0.75rem' }}
                      onClick={() => handleCompleteWithdrawal(w.id)}
                      title="ثبت واریز با شماره پیگیری"
                    >
                      <i className="fas fa-check" /> تکمیل واریز
                    </button>
                    <button
                      className="btn-outline btn-sm"
                      style={{ padding: '0.4rem 0.7rem', fontSize: '0.75rem', color: '#ef4444', borderColor: '#ef4444' }}
                      onClick={() => handleRejectWithdrawal(w.id)}
                      title="رد درخواست و بازگشت وجه"
                    >
                      <i className="fas fa-times" /> رد
                    </button>
                  </div>
                ) : (
                  <span style={{
                    padding: '0.3rem 0.7rem', borderRadius: '20px',
                    fontSize: '0.75rem', fontWeight: 'bold',
                    background: w.status === 'completed' ? '#d1fae5' : '#fee2e2',
                    color: w.status === 'completed' ? '#10b981' : '#ef4444',
                  }}>
                    {w.status === 'completed' ? `✅ واریز شده${w.adminRef ? ` (${w.adminRef})` : ''}` : '❌ رد شده'}
                  </span>
                )}
              </td>
            </tr>
          );
        };

        const totalPendingAmount = pendingWithdrawals.reduce((s, w) => s + (w.amount || 0), 0);

        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-money-check-alt" /> درخواست‌های برداشت وجه</h3>
              {pendingWithdrawals.length > 0 && (
                <span style={{
                  background: '#fef3c7', color: '#92400e',
                  padding: '0.4rem 0.9rem', borderRadius: '20px',
                  fontSize: '0.85rem', fontWeight: 'bold',
                }}>
                  {pendingWithdrawals.length} در انتظار | مجموع: {totalPendingAmount.toLocaleString('fa-IR')} تومان
                </span>
              )}
            </div>

            {pendingWithdrawals.length === 0 && allWithdrawals.length === 0 && (
              <div style={{ padding: '3rem 1rem', textAlign: 'center', color: '#94a3b8' }}>
                <i className="fas fa-inbox" style={{ fontSize: '3rem', opacity: 0.5, marginBottom: '1rem', display: 'block' }} />
                <p>هیچ درخواست برداشتی وجود ندارد</p>
              </div>
            )}

            {pendingWithdrawals.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ marginBottom: '0.8rem', color: '#f59e0b', fontSize: '0.95rem', fontWeight: 'bold' }}>
                  <i className="fas fa-clock" /> در انتظار بررسی
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table className="transaction-table">
                    <thead>
                      <tr>
                        <th>معلم</th>
                        <th>نام صاحب کارت</th>
                        <th>شماره کارت</th>
                        <th>شماره شبا</th>
                        <th>مبلغ</th>
                        <th>تاریخ</th>
                        <th>عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingWithdrawals.map((w) => renderRow(w, true))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {allWithdrawals.length > 0 && (
              <div>
                <h4 style={{ marginBottom: '0.8rem', color: '#6b7280', fontSize: '0.95rem', fontWeight: 'bold' }}>
                  <i className="fas fa-history" /> تاریخچه (۳۰ مورد آخر)
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table className="transaction-table">
                    <thead>
                      <tr>
                        <th>معلم</th>
                        <th>نام صاحب کارت</th>
                        <th>شماره کارت</th>
                        <th>شماره شبا</th>
                        <th>مبلغ</th>
                        <th>تاریخ</th>
                        <th>وضعیت</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allWithdrawals.map((w) => renderRow(w, false))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      }

      

      case 'transactions':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-credit-card" /> تراکنش‌های مالی</h3>
            </div>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div className="form-group" style={{ minWidth: '150px' }}>
                <label>نوع</label>
<select
  value={transactionFilters.type}
  onChange={(e) =>
    setTransactionFilters({ ...transactionFilters, type: e.target.value })
  }
>
  <option value="">همه</option>
  <option value="enroll">ثبت‌نام</option>
  <option value="deposit">شارژ</option>
  <option value="withdraw">برداشت</option>
<option value="refund">بازگشت وجه</option>
<option value="payment_release">واریز به معلم</option>
<option value="commission">کارمزد سکو</option>
<option value="vat">مالیات ارزش افزوده</option>
<option value="cancel_penalty">جریمه انصراف</option>
</select>
              </div>
              <div className="form-group" style={{ minWidth: '150px' }}>
                <label>از تاریخ</label>
                <input
                  type="date"
                  value={transactionFilters.dateFrom}
                  onChange={(e) =>
                    setTransactionFilters({ ...transactionFilters, dateFrom: e.target.value })
                  }
                />
              </div>
              <div className="form-group" style={{ minWidth: '150px' }}>
                <label>تا تاریخ</label>
                <input
                  type="date"
                  value={transactionFilters.dateTo}
                  onChange={(e) =>
                    setTransactionFilters({ ...transactionFilters, dateTo: e.target.value })
                  }
                />
              </div>
              <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  className="btn-outline btn-sm"
                  onClick={() => setTransactionFilters({ type: '', dateFrom: '', dateTo: '' })}
                >
                  حذف فیلترها
                </button>
              </div>
            </div>
            {filteredTransactions.length === 0 ? (
              <p className="empty-state">هیچ تراکنشی یافت نشد</p>
            ) : (
              <table className="transaction-table">
                <thead>
                  <tr>
                    <th>کاربر</th>
                    <th>مبلغ (تومان)</th>
                    <th>تاریخ</th>
                    <th>نوع</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions
                    .slice()
                    .reverse()
                    .slice(0, 50)
                    .map((t) => {
                      const u = state.users.find((x) => x.id === t.userId);
                      return (
                        <tr key={t.id}>
                          <td><i className="fas fa-user" /> {u?.name || 'نامشخص'}</td>
                          <td><strong>{t.amount.toLocaleString('fa-IR')}</strong></td>
                          <td><i className="fas fa-calendar-alt" /> {t.date}</td>
                          <td>
                            <span
                              className={`status-${
                                t.type === 'enroll'
                                  ? 'info'
                                  : t.type === 'deposit'
                                  ? 'success'
                                  : t.type === 'withdraw'
                                  ? 'warning'
                                  : 'secondary'
                              }`}
                            >
{t.type === 'enroll'
  ? `ثبت‌نام ${t.courseTitle || ''}`
  : t.type === 'deposit'
  ? 'شارژ'
  : t.type === 'withdraw'
  ? 'برداشت'
  : t.type === 'refund'
  ? 'بازگشت وجه'
  : t.type === 'payment_release'
  ? 'واریز به معلم'
  : t.type === 'pending_payment'
  ? 'در انتظار واریز'
  : t.type === 'commission'
  ? `کارمزد سکو ${t.courseTitle ? `(${t.courseTitle})` : ''}`
  : t.type === 'vat'
  ? `مالیات ارزش افزوده ${t.courseTitle ? `(${t.courseTitle})` : ''}`
  : t.type === 'cancel_penalty'
  ? `جریمه انصراف ${t.courseTitle ? `(${t.courseTitle})` : ''}`
  : t.type}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </div>
        );

      case 'settings':
        return (
          <div className="card glass pc-animate-fade-in">
            <div className="card-header">
              <h3><i className="fas fa-cog" /> تنظیمات ادمین</h3>
            </div>
            <form onSubmit={handleUpdateSettings}>
              <div className="form-group">
                <label>کارمزد سکو (درصد)</label>
                <input
                  type="number"
                  value={settings.commission}
                  onChange={(e) => setSettings({ ...settings, commission: +e.target.value })}
                  min="0"
                  max="100"
                />
              </div>
              <div className="form-group">
  <label><i className="fas fa-receipt" /> مالیات ارزش افزوده ({VAT_RATE}٪)</label>
  <input type="text" value={`${VAT_RATE}٪ (ثابت)`} disabled />
  <small style={{ color: 'var(--pc-text-muted)' }}>
    روی کل پرداختی دانش‌آموز اضافه می‌شود و به سکو می‌رسد.
  </small>
</div>
<div className="form-group">
  <label><i className="fas fa-gavel" /> جریمه انصراف دانش‌آموز ({CANCEL_PENALTY_RATE}٪)</label>
  <input type="text" value={`${CANCEL_PENALTY_RATE}٪ (ثابت)`} disabled />
  <small style={{ color: 'var(--pc-text-muted)' }}>
    در صورت انصراف دانش‌آموز، از قیمت پایه کسر و به سکو می‌رسد.
  </small>
</div>
              <div className="form-group">
                <label>حداکثر دانش‌آموز در کلاس</label>
                <input
                  type="number"
                  value={settings.maxStudents}
                  onChange={(e) => setSettings({ ...settings, maxStudents: +e.target.value })}
                  min="1"
                />
              </div>
              <div className="form-group">
                <label><i className="fas fa-toggle-on" /> فعال‌سازی ثبت‌نام معلمان</label>
                <select
                  value={settings.enableTeacherRegistration}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      enableTeacherRegistration: e.target.value === 'true',
                    })
                  }
                >
                  <option value="true">فعال</option>
                  <option value="false">غیرفعال</option>
                </select>
              </div>
              <div className="form-group">
                <label><i className="fas fa-link" /> saleId پیش‌فرض رومیت</label>
                <select
                  value={settings.defaultSaleId || selectedSaleId}
                  onChange={(e) => setSettings({ ...settings, defaultSaleId: e.target.value })}
                >
                  {services.map((s) => (
                    <option key={s.saleId} value={s.saleId}>
                      {s.packageName} (حداکثر {s.maxParticipants} نفر)
                    </option>
                  ))}
                </select>
                <small style={{ color: 'var(--pc-text-muted)' }}>
                  این saleId برای ایجاد کلاس‌ها در رومیت استفاده می‌شود
                </small>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  <i className="fas fa-save" /> ذخیره تنظیمات
                </button>
              </div>
            </form>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="panel-wrapper">
      <div className="pc-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`fas ${tab.icon}`} /> {tab.label}
          </button>
        ))}
      </div>
      {renderContent()}

      {/* مودال مشاهده کاربر */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={selectedUser?.name}>
        {selectedUser && (
          <>
            <p><strong>نقش:</strong>{' '}
              {selectedUser.role === 'teacher' ? 'معلم'
                : selectedUser.role === 'admin' ? 'ادمین'
                : 'دانش‌آموز'}
            </p>
            <p><strong>ایمیل:</strong> {selectedUser.email}</p>
            <p><strong>کیف پول:</strong> {(selectedUser.wallet || 0).toLocaleString('fa-IR')} تومان</p>
            {selectedUser.role === 'student' && (
              <p><strong>تعداد کلاس‌ها:</strong> {selectedUser.enrolledCourses?.length || 0}</p>
            )}
            <p><strong>Roomeet Member ID:</strong> {selectedUser.roomeetMemberId || '—'}</p>
            <p><strong>وضعیت:</strong> {selectedUser.active !== false ? 'فعال' : 'غیرفعال'}</p>
          </>
        )}
      </Modal>

      {/* مودال افزودن کاربر */}
      <Modal isOpen={addUserModal} onClose={() => setAddUserModal(false)} title="افزودن کاربر جدید">
        <form onSubmit={handleAddUser}>
          <div className="form-group">
            <label>نام کامل</label>
            <input
              type="text"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>شماره تلفن</label>
            <input
              type="text"
              value={newUser.phone}
              onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>رمز عبور</label>
            <input
              type="text"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              placeholder="پیش‌فرض: 123456"
            />
          </div>
          <div className="form-group">
            <label>کیف پول (تومان)</label>
            <input
              type="number"
              value={newUser.wallet}
              onChange={(e) => setNewUser({ ...newUser, wallet: +e.target.value })}
              min="0"
            />
          </div>
          <div className="form-group">
            <label>نقش</label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              <option value="student">دانش‌آموز</option>
              <option value="teacher">معلم</option>
            </select>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'در حال افزودن...' : 'افزودن کاربر'}
            </button>
          </div>
        </form>
      </Modal>

      {/* ✅ D32: مودال ایجاد کلاس با همه‌ی فیلدها */}
      <Modal isOpen={roomModal} onClose={() => setRoomModal(false)} title="ایجاد کلاس جدید در رومیت">
        <form onSubmit={handleCreateRoom}>
          <div className="form-group">
            <label>نام کلاس <span style={{ color: '#f43f5e' }}>*</span></label>
            <input
              type="text"
              value={roomForm.name}
              onChange={(e) => setRoomForm({ ...roomForm, name: e.target.value })}
              required
              placeholder="مثال: جلسه ریاضی کنکور"
            />
          </div>
          <div className="form-group">
            <label>بسته سرویس</label>
            <select value={selectedSaleId} onChange={(e) => setSelectedSaleId(e.target.value)}>
              {services.map((s) => (
                <option key={s.saleId} value={s.saleId}>
                  {s.packageName} (حداکثر {s.maxParticipants} نفر)
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>مدت زمان (دقیقه)</label>
              <input
                type="number"
                value={roomForm.duration}
                onChange={(e) => setRoomForm({ ...roomForm, duration: +e.target.value })}
                min="10"
                max="300"
              />
            </div>
            <div className="form-group">
              <label>زبان</label>
              <select
                value={roomForm.language}
                onChange={(e) => setRoomForm({ ...roomForm, language: e.target.value })}
              >
                <option value="fa">فارسی</option>
                <option value="en">انگلیسی</option>
                <option value="es">اسپانیایی</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>قیمت (تومان)</label>
              <input
                type="number"
                value={roomForm.price}
                onChange={(e) => setRoomForm({ ...roomForm, price: +e.target.value })}
                min="0"
                step="1000"
                placeholder="۰ برای کلاس رایگان"
              />
            </div>
            <div className="form-group">
              <label>ظرفیت</label>
              <input
                type="number"
                value={roomForm.capacity}
                onChange={(e) => setRoomForm({ ...roomForm, capacity: +e.target.value })}
                min="1"
                max="50"
              />
            </div>
          </div>
          <div className="form-group">
            <label>مدرس (اختیاری)</label>
            <select
              value={roomForm.teacherId}
              onChange={(e) => setRoomForm({ ...roomForm, teacherId: e.target.value })}
            >
              <option value="">بدون مدرس (فقط مدیر)</option>
              {users
                .filter((u) => u.role === 'teacher' && u.active !== false)
                .map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacher.name} - {teacher.phone}
                  </option>
                ))}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>تاریخ شروع (شمسی) <span style={{ color: '#f43f5e' }}>*</span></label>
              <PersianDatePicker
                value={roomForm.date}
                onChange={(newDate) => setRoomForm({ ...roomForm, date: newDate })}
              />
            </div>
            <div className="form-group">
              <label>ساعت شروع <span style={{ color: '#f43f5e' }}>*</span></label>
              <input
                type="time"
                value={roomForm.time}
                onChange={(e) => setRoomForm({ ...roomForm, time: e.target.value })}
                required
              />
            </div>
          </div>

          {/* ✅ D32: بخش پیام‌ها */}
          <div className="form-group">
            <label>پیام خوش‌آمدگویی</label>
            <input
              type="text"
              value={roomForm.welcomeMessage}
              onChange={(e) => setRoomForm({ ...roomForm, welcomeMessage: e.target.value })}
              placeholder="پیامی که به شرکت‌کنندگان نمایش داده می‌شود"
            />
          </div>
          <div className="form-group">
            <label>پیام مدیر</label>
            <input
              type="text"
              value={roomForm.moderatorOnlyMessage}
              onChange={(e) => setRoomForm({ ...roomForm, moderatorOnlyMessage: e.target.value })}
              placeholder="پیام اختصاصی برای مدیر"
            />
          </div>

          {/* ✅ D32: تنظیمات ساده */}
          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={roomForm.guest}
                  onChange={(e) => setRoomForm({ ...roomForm, guest: e.target.checked })}
                />{' '}
                مجاز بودن مهمان‌ها
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={roomForm.startWithModerator}
                  onChange={(e) => setRoomForm({ ...roomForm, startWithModerator: e.target.checked })}
                />{' '}
                شروع با مدیر
              </label>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={roomForm.autoStartRecording}
                  onChange={(e) => setRoomForm({ ...roomForm, autoStartRecording: e.target.checked })}
                />{' '}
                ضبط خودکار
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={roomForm.muteOnStart}
                  onChange={(e) => setRoomForm({ ...roomForm, muteOnStart: e.target.checked })}
                />{' '}
                بی‌صدا در شروع
              </label>
            </div>
          </div>

          {/* ✅ D32: دکمه‌ی Advanced */}
          <div className="form-group" style={{ marginTop: '1rem' }}>
            <button
              type="button"
              className="btn-outline btn-sm"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              <i className={`fas ${showAdvancedOptions ? 'fa-chevron-up' : 'fa-chevron-down'}`} />{' '}
              {showAdvancedOptions ? 'بستن تنظیمات پیشرفته' : 'نمایش تنظیمات پیشرفته'}
            </button>
          </div>

          {showAdvancedOptions && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={roomForm.allowDownloadRecordings}
                      onChange={(e) => setRoomForm({ ...roomForm, allowDownloadRecordings: e.target.checked })}
                    />{' '}
                    اجازه دانلود ضبط
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={roomForm.webcamsOnlyForModerat}
                      onChange={(e) => setRoomForm({ ...roomForm, webcamsOnlyForModerat: e.target.checked })}
                    />{' '}
                    فقط مدیر دوربین داشته باشد
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={roomForm.allowModsToEjectCameras}
                      onChange={(e) => setRoomForm({ ...roomForm, allowModsToEjectCameras: e.target.checked })}
                    />{' '}
                    اخراج دوربین توسط مدیر
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={roomForm.disablePresentation}
                      onChange={(e) => setRoomForm({ ...roomForm, disablePresentation: e.target.checked })}
                    />{' '}
                    غیرفعال کردن ارائه
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={roomForm.lockSettingsDisablePrivateChat}
                      onChange={(e) => setRoomForm({ ...roomForm, lockSettingsDisablePrivateChat: e.target.checked })}
                    />{' '}
                    غیرفعال چت خصوصی
                  </label>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={roomForm.lockSettingsDisablePublicChat}
                      onChange={(e) => setRoomForm({ ...roomForm, lockSettingsDisablePublicChat: e.target.checked })}
                    />{' '}
                    غیرفعال چت عمومی
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={roomForm.lockSettingsHideUserList}
                    onChange={(e) => setRoomForm({ ...roomForm, lockSettingsHideUserList: e.target.checked })}
                  />{' '}
                  مخفی کردن لیست کاربران
                </label>
              </div>
              <div className="form-group">
                <label>تأخیر پایان در صورت نبود مدیر (دقیقه)</label>
                <input
                  type="number"
                  value={roomForm.endWhenNoModeratorDelayInMinutes}
                  onChange={(e) => setRoomForm({
                    ...roomForm,
                    endWhenNoModeratorDelayInMinutes: +e.target.value,
                  })}
                  min="0"
                  max="60"
                />
              </div>
              <div className="form-group">
                <label>URL خروج</label>
                <input
                  type="text"
                  value={roomForm.logoutURL}
                  onChange={(e) => setRoomForm({ ...roomForm, logoutURL: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>
            </>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn-outline"
              onClick={() => {
                setRoomModal(false);
                setShowAdvancedOptions(false);
              }}
            >
              لغو
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'در حال ایجاد...' : 'ایجاد کلاس'}
            </button>
          </div>
        </form>
      </Modal>

      {/* مودال ویرایش درخواست */}
      <Modal
        isOpen={showEditRequestModal}
        onClose={() => setShowEditRequestModal(false)}
        title="ویرایش درخواست کلاس"
      >
        {editRequestForm && (
          <form onSubmit={handleEditRequestSubmit}>
            <div className="form-group">
              <label>عنوان کلاس</label>
              <input
                type="text"
                value={editRequestForm.title || ''}
                onChange={(e) =>
                  setEditRequestForm({ ...editRequestForm, title: e.target.value })
                }
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>پایه</label>
                <select
                  value={editRequestForm.grade || ''}
                  onChange={(e) =>
                    setEditRequestForm({ ...editRequestForm, grade: e.target.value })
                  }
                >
                  {gradeOptions
                    .filter((g) => g !== 'همه پایه‌ها')
                    .map((g) => <option key={g}>{g}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>رشته</label>
                <select
                  value={editRequestForm.field || ''}
                  onChange={(e) =>
                    setEditRequestForm({ ...editRequestForm, field: e.target.value })
                  }
                >
                  {fieldOptions.map((f) => <option key={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>درس</label>
              <input
                type="text"
                value={editRequestForm.subject || ''}
                onChange={(e) =>
                  setEditRequestForm({ ...editRequestForm, subject: e.target.value })
                }
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>تاریخ (شمسی)</label>
                <PersianDatePicker
                  value={editRequestForm.date || ''}
                  onChange={(newDate) =>
                    setEditRequestForm({ ...editRequestForm, date: newDate })
                  }
                />
              </div>
              <div className="form-group">
                <label>ساعت</label>
                <input
                  type="time"
                  value={editRequestForm.time || ''}
                  onChange={(e) =>
                    setEditRequestForm({ ...editRequestForm, time: e.target.value })
                  }
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>قیمت (تومان)</label>
                <input
                  type="number"
                  value={editRequestForm.price || 0}
                  onChange={(e) =>
                    setEditRequestForm({ ...editRequestForm, price: +e.target.value })
                  }
                  min="1000"
                  required
                />
              </div>
              <div className="form-group">
                <label>ظرفیت</label>
                <select
                  value={editRequestForm.capacity || 2}
                  onChange={(e) =>
                    setEditRequestForm({
                      ...editRequestForm,
                      capacity: +e.target.value,
                    })
                  }
                >
                  {capacityOptions.map((n) => <option key={n}>{n}</option>)}
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button
                type="button"
                className="btn-outline"
                onClick={() => setShowEditRequestModal(false)}
              >
                لغو
              </button>
              <button type="submit" className="btn-primary">ذخیره تغییرات</button>
            </div>
          </form>
        )}
      </Modal>

      {/* مودال ویرایش کاربر */}
      <Modal isOpen={editUserModal} onClose={() => setEditUserModal(false)} title="ویرایش کاربر">
        {editUserForm && (
          <form onSubmit={handleEditUserSubmit}>
            <div className="form-group">
              <label>نام کامل</label>
              <input
                type="text"
                value={editUserForm.name || ''}
                onChange={(e) => setEditUserForm({ ...editUserForm, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>شماره تلفن</label>
              <input
                type="text"
                value={editUserForm.phone || ''}
                onChange={(e) => setEditUserForm({ ...editUserForm, phone: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>ایمیل</label>
              <input
                type="email"
                value={editUserForm.email || ''}
                onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>نقش</label>
              <select
                value={editUserForm.role || 'student'}
                onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
              >
                <option value="student">دانش‌آموز</option>
                <option value="teacher">معلم</option>
                <option value="admin">ادمین</option>
              </select>
              <small style={{ color: 'var(--pc-text-muted)' }}>
                ⚠️ با تغییر نقش، داده‌های مخصوص نقش قبلی (کلاس‌ها یا درآمد معلق) پاک می‌شوند.
              </small>
            </div>
            <div className="form-group">
              <label>وضعیت فعال</label>
              <select
                value={editUserForm.active !== false ? 'true' : 'false'}
                onChange={(e) =>
                  setEditUserForm({
                    ...editUserForm,
                    active: e.target.value === 'true',
                  })
                }
              >
                <option value="true">فعال</option>
                <option value="false">غیرفعال</option>
              </select>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-outline" onClick={() => setEditUserModal(false)}>
                لغو
              </button>
              <button type="submit" className="btn-primary">ذخیره تغییرات</button>
            </div>
          </form>
        )}
      </Modal>

      {/* مودال نوتیفیکیشن جمعی */}
      <Modal
        isOpen={bulkNotificationModal}
        onClose={() => setBulkNotificationModal(false)}
        title="ارسال نوتیفیکیشن جمعی"
      >
        <form onSubmit={handleBulkNotification}>
          <div className="form-group">
            <label>متن پیام</label>
            <textarea
              rows="4"
              value={bulkNotificationData.message}
              onChange={(e) =>
                setBulkNotificationData({ ...bulkNotificationData, message: e.target.value })
              }
              placeholder="پیام خود را وارد کنید..."
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>نوع پیام</label>
              <select
                value={bulkNotificationData.type}
                onChange={(e) =>
                  setBulkNotificationData({ ...bulkNotificationData, type: e.target.value })
                }
              >
                <option value="info">اطلاع‌رسانی</option>
                <option value="success">موفقیت</option>
                <option value="warning">هشدار</option>
                <option value="error">خطا</option>
              </select>
            </div>
            <div className="form-group">
              <label>دریافت‌کنندگان</label>
              <select
                value={bulkNotificationData.role}
                onChange={(e) =>
                  setBulkNotificationData({ ...bulkNotificationData, role: e.target.value })
                }
              >
                <option value="">همه کاربران</option>
                <option value="student">دانش‌آموزان</option>
                <option value="teacher">معلمان</option>
                <option value="admin">ادمین‌ها</option>
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-outline" onClick={() => setBulkNotificationModal(false)}>
              لغو
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'در حال ارسال...' : 'ارسال پیام'}
            </button>
          </div>
        </form>
      </Modal>

      {/* مودال ثبت‌نام اجباری */}
      <Modal
        isOpen={forceEnrollModal}
        onClose={() => setForceEnrollModal(false)}
        title={`ثبت‌نام اجباری در کلاس ${forceEnrollCourse?.title || ''}`}
      >
        {forceEnrollStudents.length === 0 ? (
          <p className="empty-state">همه دانش‌آموزان قبلاً در این کلاس ثبت‌نام کرده‌اند.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, maxHeight: '400px', overflowY: 'auto' }}>
            {forceEnrollStudents.map((u) => (
              <li
                key={u.id}
                style={{
                  padding: '0.5rem 0',
                  borderBottom: '1px solid var(--pc-border-color)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span>
                  {u.name} ({u.phone}) — کیف پول: {(u.wallet || 0).toLocaleString('fa-IR')}
                </span>
                <button
                  className="btn-primary btn-sm"
                  onClick={async () => {
                    await handleForceEnroll(u.id, forceEnrollCourse.id);
                    setForceEnrollStudents((prev) => prev.filter((s) => s.id !== u.id));
                  }}
                >
                  ثبت‌نام
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="form-actions">
          <button className="btn-outline" onClick={() => setForceEnrollModal(false)}>بستن</button>
        </div>
      </Modal>

      {/* مودال لیست دانش‌آموزان */}
      <Modal
        isOpen={showStudentsModalAdmin}
        onClose={() => setShowStudentsModalAdmin(false)}
        title={`لیست دانش‌آموزان کلاس ${selectedCourseTitleAdmin}`}
      >
        {selectedCourseStudentsAdmin.length === 0 ? (
          <p className="empty-state">هیچ دانش‌آموزی ثبت‌نام نکرده است</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {selectedCourseStudentsAdmin.map((s, idx) => (
              <li
                key={idx}
                style={{
                  padding: '0.5rem 0',
                  borderBottom: '1px solid var(--pc-border-color)',
                }}
              >
                <strong>{s.fullName}</strong> – {s.phone}
                <span style={{ fontSize: '0.8rem', color: 'var(--pc-text-muted)' }}>
                  {' '}(ثبت‌نام در {s.enrolledAt})
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="form-actions">
          <button className="btn-outline" onClick={() => setShowStudentsModalAdmin(false)}>بستن</button>
        </div>
      </Modal>

      {/* مودال ویرایش اتاق Roomeet */}
      <Modal
        isOpen={showEditRoomModal}
        onClose={() => setShowEditRoomModal(false)}
        title="ویرایش کلاس در رومیت"
      >
        <form onSubmit={handleUpdateRoom}>
          <div className="form-group">
            <label>نام کلاس</label>
            <input
              type="text"
              value={editRoomForm.name}
              onChange={(e) => setEditRoomForm({ ...editRoomForm, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>بسته سرویس</label>
            <select
              value={editRoomForm.saleId}
              onChange={(e) => setEditRoomForm({ ...editRoomForm, saleId: e.target.value })}
            >
              {services.map((s) => (
                <option key={s.saleId} value={s.saleId}>
                  {s.packageName} (حداکثر {s.maxParticipants} نفر)
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>مدت زمان (دقیقه)</label>
              <input
                type="number"
                value={editRoomForm.duration}
                onChange={(e) => setEditRoomForm({ ...editRoomForm, duration: +e.target.value })}
                min="10"
                max="300"
                required
              />
            </div>
            <div className="form-group">
              <label>زبان</label>
              <select
                value={editRoomForm.language}
                onChange={(e) => setEditRoomForm({ ...editRoomForm, language: e.target.value })}
              >
                <option value="fa">فارسی</option>
                <option value="en">انگلیسی</option>
                <option value="es">اسپانیایی</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>تاریخ شروع (شمسی)</label>
              <PersianDatePicker
                value={editRoomForm.date}
                onChange={(newDate) => setEditRoomForm({ ...editRoomForm, date: newDate })}
              />
            </div>
            <div className="form-group">
              <label>ساعت شروع</label>
              <input
                type="time"
                value={editRoomForm.time}
                onChange={(e) => setEditRoomForm({ ...editRoomForm, time: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="form-actions">
            <button type="button" className="btn-outline" onClick={() => setShowEditRoomModal(false)}>
              لغو
            </button>
            <button type="submit" className="btn-primary" disabled={isLoading}>
              {isLoading ? 'در حال ویرایش...' : 'ویرایش کلاس'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ================================================================
// کامپوننت اصلی
// ================================================================
function AdminPrivateClassPageContent() {
  const { user, updateUser } = useAuth();
  const { state, dispatch, refreshData, internalApi, roomeetApi, loading, error } = useData();
  const showToast = useToast();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // ✅ E26: پاس‌دادن users به authGate برای چک حذف کاربر
  const authGate = useRoomeetAuthGate({
    user,
    updateUser,
    roomeetApi,
    requiredRole: 'admin',
    users: state.users,
  });
  const isRoomeetReady = authGate.isReady;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  const handleSearch = useCallback(
    async (query) => {
      setSearchQuery(query);
      if (query.trim().length < 2) {
        setSearchResults([]);
        setIsSearchOpen(false);
        return;
      }
      const courses = await internalApi.getCourses();
      const results = courses.filter(
        (c) =>
          c.title?.includes(query) ||
          c.teacher?.includes(query) ||
          c.subject?.includes(query) ||
          c.grade?.includes(query) ||
          c.field?.includes(query) ||
          c.description?.includes(query)
      );
      setSearchResults(results);
      setIsSearchOpen(true);
    },
    [internalApi]
  );

  useSearchShortcut(inputRef);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { scrollProgress, headerScrolled } = useScrollProgress();

  if (!user) {
    return (
      <div className="private-class-page">
        <div className="pc-content" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div className="card glass" style={{ maxWidth: '500px', margin: '0 auto', padding: '3rem 2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔒</div>
            <h2 style={{ color: 'var(--pc-text-primary)', marginBottom: '0.5rem' }}>
              لطفاً ابتدا وارد شوید
            </h2>
            <p style={{ color: 'var(--pc-text-muted)', marginBottom: '2rem' }}>
              برای دسترسی به این صفحه، باید وارد حساب کاربری خود شوید.
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate('/login')}
              style={{ fontSize: '1.1rem', padding: '0.8rem 2.5rem' }}
            >
              <i className="fas fa-arrow-left" /> رفتن به صفحه ورود
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (user.role !== 'admin') {
    return (
      <div className="private-class-page">
        <div className="pc-content" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div
            className="card glass"
            style={{
              maxWidth: '600px',
              margin: '0 auto',
              padding: '3rem 2rem',
              borderColor: '#f43f5e',
            }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🚫</div>
            <h2 style={{ color: 'var(--pc-text-primary)', marginBottom: '0.5rem' }}>
              دسترسی غیرمجاز
            </h2>
            <p style={{ color: 'var(--pc-text-muted)', marginBottom: '2rem' }}>
              این صفحه مخصوص ادمین‌ها است.
            </p>
            <button
              className="btn-primary"
              onClick={() => navigate('/home')}
              style={{ fontSize: '1.1rem', padding: '0.8rem 2.5rem' }}
            >
              <i className="fas fa-arrow-left" /> بازگشت به خانه
            </button>
          </div>
        </div>
      </div>
    );
  }

  const notifCount = state.notifications.filter((n) => !n.read).length;
  const panelProps = {
    user,
    state,
    dispatch,
    refreshData,
    showToast,
    internalApi,
    roomeetApi,
    loading,
    isRoomeetReady,
  };

  return (
    <AuthGate
      status={authGate.status}
      roleLabel="ادمین"
      roleIcon="fa-user-tie"
      requiredRole="manager"
      loginError={authGate.loginError}
      onLogin={authGate.login}
    >
      <div className="private-class-page">
        <div className="scroll-progress-bar" style={{ width: scrollProgress + '%' }} />
        <PageParticles />
        <header className={`pc-header ${headerScrolled ? 'scrolled' : ''}`}>
          <div className="header-left">
            <div className="logo-wrapper">
              <div className="logo-icon">
                <i className="fas fa-user-tie" />
                <div className="logo-pulse" />
              </div>
              <h1>
                کلاس خصوصی<span className="role-badge">ادمین</span>
              </h1>
            </div>
            <div className="header-subtitle">
              <span className="subtitle-text">🎯 مدیریت کلاس‌های خصوصی</span>
            </div>
          </div>
          <div className="header-right">
            <div className="search-box-wrapper" ref={searchRef}>
              <div className={`search-box ${isSearchOpen && searchResults.length > 0 ? 'active' : ''}`}>
                <i className="fas fa-search" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="جستجوی کاربر، کلاس، تراکنش..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchQuery.trim().length >= 2 && setIsSearchOpen(true)}
                />
                <span className="type-cursor" />
                <span className="search-shortcut">⌘K</span>
                {searchQuery && (
                  <button
                    className="search-clear"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      setIsSearchOpen(false);
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
              {isSearchOpen && searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.slice(0, 6).map((c) => (
                    <div
                      key={c.id}
                      className="search-result-item"
                      onClick={() => {
                        setIsSearchOpen(false);
                        showToast(`«${c.title}» — برای مشاهده‌ی جزئیات، به تب «مدیریت کلاس‌ها» بروید`, 'info');
                      }}
                    >
                      <div className="result-icon" style={{ background: c.color || '#0b2b5c' }}>
                        <i className={`fas ${c.icon || 'fa-chalkboard'}`} />
                      </div>
                      <div className="result-info">
                        <div className="result-title">{c.title}</div>
                        <div className="result-meta">
                          {c.teacher} • {(c.price || 0).toLocaleString('fa-IR')} تومان
                        </div>
                      </div>
                      <button
                        className="result-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsSearchOpen(false);
                          showToast(`«${c.title}» — برای مشاهده‌ی جزئیات، به تب «مدیریت کلاس‌ها» بروید`, 'info');
                        }}
                      >
                        مشاهده
                      </button>
                    </div>
                  ))}
                  {searchResults.length > 6 && (
                    <div className="search-more">+ {searchResults.length - 6} نتیجه دیگر</div>
                  )}
                </div>
              )}
            </div>
            <button className="theme-toggle" onClick={toggleTheme} aria-label="تغییر تم">
              <span className="icon">{theme === 'light' ? '🌙' : '☀️'}</span>
            </button>
            {/*<div
              className="notification-wrapper"
              onClick={() => showToast(`${notifCount} نوتیفیکیشن جدید`, 'info')}
              aria-label="اعلان‌ها"
            >
              <i className="fas fa-bell">
                {notifCount > 0 && <span className="notif-badge">{notifCount}</span>}
              </i>
              <div className="notification-dot" />
            </div>
            <div className="avatar-wrapper">
              <img
                src={`https://ui-avatars.com/api/?name=${(user.name || 'کاربر').replace(/ /g, '+')}&background=0b2b5c&color=fff&size=46`}
                alt="آواتار"
                loading="lazy"
              />
              <div className="avatar-ring" />
              <div className="avatar-status" />
            </div>*/}
          </div>
        </header>
        <div className="pc-content">
          {error && (
            <div className="card glass" style={{ borderColor: '#f43f5e', marginBottom: '1.5rem' }}>
              <p style={{ color: '#f43f5e', fontWeight: 'bold' }}>
                ⚠️ خطا در بارگذاری داده‌ها: {error}
              </p>
              <button className="btn-primary btn-sm" onClick={() => refreshData()}>تلاش مجدد</button>
            </div>
          )}
          {loading ? (
            <div className="skeleton-card" style={{ height: '300px', borderRadius: '28px' }} />
          ) : (
            <AdminPanel {...panelProps} />
          )}
        </div>
      </div>
    </AuthGate>
  );
}

const MemoizedAdminPrivateClassPageContent = React.memo(AdminPrivateClassPageContent);

export default function AdminPrivateClassPage() {
  return (
    <PrivateClassProviders>
      <MemoizedAdminPrivateClassPageContent />
    </PrivateClassProviders>
  );
}